/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CatalogueContent } from './catalogue/CatalogueShared';
import { useCatalog } from '../hooks/useCatalog';
import CatalogPill, { CatalogPillsStack } from '../components/CatalogPill';
import InlineCatalogPicker from '../components/InlineCatalogPicker';
import FormalistePicker from '../components/FormalistePicker';
import ImportsPage from './Imports';
import PlatformBackup from './PlatformBackup';
import JalCell from './JalCell';

const API = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';
const api = axios.create({ baseURL: API });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('lexback_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

// ─── Design tokens ───────────────────────────────────────────────────────────
const D = {
  bg:    '#13131A',
  s1:    '#1C1C28',
  s2:    '#252533',
  b1:    '#3A3A4A',
  b2:    'rgba(255,255,255,0.16)',
  tx:    '#EDEAE4',
  tx2:   '#B8B8C4',
  tx3:   '#7E7E8E',
  or:    '#D4A843',
  green: '#2ECC8A',
  red:   '#EF5B5B',
  blue:  '#5B8DEF',
  amber: '#F0A843',
};

const card = { background:D.s1, border:`1px solid ${D.b1}`, borderRadius:'10px' };

function Badge({ label, color, bg }) {
  return <span style={{ fontSize:'10px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color, background:bg||`${color}18` }}>{label}</span>;
}

function KpiCard({ label, value, delta, color, sub, onClick }) {
  return (
    <div onClick={onClick} style={{ ...card, padding:'14px 16px', cursor:onClick?'pointer':'default' }}>
      <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:D.tx3, marginBottom:'5px' }}>{label}</div>
      <div style={{ fontFamily:'Syne, sans-serif', fontSize:'22px', fontWeight:700, color:color||D.tx, lineHeight:1 }}>{value}</div>
      {delta && <div style={{ fontSize:'10px', color:D.tx3, marginTop:'4px' }}>{delta}</div>}
      {sub && <div style={{ fontSize:'10px', color:D.tx3, marginTop:'4px' }}>{sub}</div>}
    </div>
  );
}

// ─── NAV ─────────────────────────────────────────────────────────────────────
const NAV = [
  { id:'overview',   label:'Vue d\'ensemble' },
  { id:'clients',    label:'Clients' },
  { id:'equipes',    label:'Équipes' },
  { id:'operations', label:'Opérations' },
  { id:'alertes',    label:'Alertes' },
  { id:'facturation',label:'Facturation' },
  { id:'parametres', label:'Paramètres' },
];

function AdminLayout({ children, active, setActive, badges = {} }) {
  return (
    <div style={{ display:'flex', minHeight:'100vh', background:D.bg, color:D.tx, fontFamily:"'DM Sans', sans-serif" }}>
      {/* Sidebar */}
      <div style={{ width:'188px', background:D.s1, borderRight:`1px solid ${D.b1}`, display:'flex', flexDirection:'column', flexShrink:0 }}>
        <div style={{ padding:'14px 16px', borderBottom:`1px solid ${D.b1}`, display:'flex', alignItems:'center', gap:'8px' }}>
          <span style={{ fontFamily:'Syne, sans-serif', fontSize:'15px', fontWeight:700, color:D.or }}>Lexback</span>
          <span style={{ fontSize:'9px', background:'rgba(212,168,67,0.15)', color:D.or, padding:'1px 6px', borderRadius:'3px', fontWeight:500 }}>ADMIN</span>
        </div>
        <nav style={{ flex:1, padding:'6px 0' }}>
          {NAV.map(item => {
            const badge = badges[item.id];
            return (
              <div key={item.id} onClick={() => setActive(item.id)} style={{
                display:'flex', alignItems:'center', padding:'8px 16px', cursor:'pointer', fontSize:'12px',
                color: active===item.id ? D.or : D.tx2,
                borderLeft: active===item.id ? `2px solid ${D.or}` : '2px solid transparent',
                background: active===item.id ? 'rgba(212,168,67,0.06)' : 'transparent',
                transition:'all 0.12s',
              }}>
                <span style={{ flex:1 }}>{item.label}</span>
                {badge > 0 && <span style={{ background:D.red, color:'#fff', fontSize:'9px', fontWeight:700, padding:'1px 5px', borderRadius:'8px' }}>{badge}</span>}
              </div>
            );
          })}
        </nav>
        <div style={{ margin:'8px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'8px', padding:'8px 10px' }}>
          <div style={{ fontSize:'11px', color:D.tx }}>Swap Business</div>
          <div style={{ fontSize:'9px', color:D.tx3 }}>Admin · Sara D.</div>
        </div>
      </div>
      {/* Main */}
      <div style={{ flex:1, overflow:'auto', display:'flex', flexDirection:'column' }}>{children}</div>
    </div>
  );
}

// ─── OVERVIEW ────────────────────────────────────────────────────────────────
function Overview({ setActive }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [alertes, setAlertes]       = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [wsRes, alRes] = await Promise.all([
          api.get('/api/admin/workspaces-enriched'),
          api.get('/api/admin/alertes'),
        ]);
        if (!cancel) {
          setWorkspaces(wsRes.data || []);
          setAlertes(alRes.data || []);
        }
      } catch (err) {
        if (!cancel) {
          // Fallback : essayer le endpoint de base
          try {
            const r = await api.get('/api/admin/workspaces');
            if (!cancel) setWorkspaces(r.data || []);
          } catch {}
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const ws = workspaces;
  const totalDos  = ws.reduce((s,w) => s+(w.dossiers_ce_mois||0), 0);
  const totalWal  = ws.reduce((s,w) => s+Number(w.wallet_balance||0), 0);
  const totalCA   = ws.reduce((s,w) => s+Number(w.forfait_mensuel||0), 0);
  const nAlertes  = alertes.length;
  const nCrit     = alertes.filter(a => a.level === 'crit').length;

  const PLAN_COLOR = { enterprise:'#D4A843', pro:'#5B8DEF', starter:D.tx3 };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'à l\'instant';
    if (diff < 3600) return `il y a ${Math.floor(diff/60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff/3600)} h`;
    return `il y a ${Math.floor(diff/86400)} j`;
  };

  return (
    <div style={{ padding:'1.25rem 1.5rem', flex:1 }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem' }}>
        <div>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'18px', fontWeight:600, color:D.tx }}>Command Center</div>
          <div style={{ fontSize:'11px', color:D.tx3, marginTop:'2px' }}>Vue globale · {ws.length} clients · {new Date().toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long'})}</div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={() => setActive('clients')} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 14px', borderRadius:'7px', fontSize:'12px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
            + Nouveau client
          </button>
        </div>
      </div>

      {/* KPIs globaux */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'10px', marginBottom:'1.25rem' }}>
        <KpiCard label="CA mensuel"       value={`${totalCA.toLocaleString('fr-FR')} €`} color={D.green} delta="Forfaits cumulés" onClick={() => setActive('facturation')} />
        <KpiCard label="Dossiers ce mois" value={totalDos.toLocaleString('fr-FR')}         delta="Toutes LegalTechs" onClick={() => setActive('operations')} />
        <KpiCard label="Wallet total"     value={`${totalWal.toLocaleString('fr-FR')} €`}  delta="Avances INPI" />
        <KpiCard label="Clients actifs"   value={ws.filter(w => w.status === 'active').length} delta={`${ws.filter(w=>w.plan==='enterprise').length} Enterprise`} />
        <KpiCard label="Alertes actives"  value={nAlertes} color={nAlertes>0?D.red:D.green} delta={nCrit>0?`${nCrit} critique${nCrit>1?'s':''}`:"Tout va bien"} onClick={() => setActive('alertes')} />
      </div>

      {/* Tableau clients */}
      <div style={{ ...card, overflow:'hidden', marginBottom:'1rem' }}>
        <div style={{ padding:'12px 16px', borderBottom:`1px solid ${D.b1}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx }}>Clients — Vue temps réel</div>
          <button onClick={() => setActive('clients')} style={{ background:'transparent', border:`1px solid ${D.b1}`, color:D.tx2, padding:'4px 10px', borderRadius:'5px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Gérer →</button>
        </div>
        {loading && ws.length === 0 ? (
          <div style={{ padding:'2rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>Chargement…</div>
        ) : ws.length === 0 ? (
          <div style={{ padding:'2rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>Aucun client pour l'instant.</div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${D.b1}` }}>
                {['Client','Plan','Dossiers/mois','Wallet','Formalistes','SLA','Statut'].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'9px 14px', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:D.tx3, fontWeight:400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ws.map((w,i) => {
                const walBas = Number(w.wallet_balance||0) < Number(w.wallet_alert_threshold||3000);
                const slaOk  = (w.sla_ok||95) >= 90;
                return (
                  <tr key={w.id} style={{ borderBottom:i<ws.length-1?`1px solid ${D.b1}`:'none', cursor:'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background=D.s2}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                    onClick={() => setActive('clients')}
                  >
                    <td style={{ padding:'10px 14px', fontSize:'12px', fontWeight:500, color:D.tx }}>{w.name}</td>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ fontSize:'10px', fontWeight:500, color:PLAN_COLOR[w.plan]||D.tx3 }}>{w.plan}</span>
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:'12px', color:D.tx2 }}>{(w.dossiers_ce_mois||0).toLocaleString('fr-FR')}</td>
                    <td style={{ padding:'10px 14px', fontSize:'12px', fontWeight:500, color:walBas?D.red:D.green }}>
                      {Number(w.wallet_balance||0).toLocaleString('fr-FR')} €
                      {walBas && <span style={{ fontSize:'9px', marginLeft:'4px' }}>⚠</span>}
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:'12px', color:D.tx2 }}>{w.formalistes||0} ETP</td>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ fontSize:'11px', fontWeight:500, color:slaOk?D.green:D.red }}>{(w.sla_ok||95).toFixed(0)}%</span>
                    </td>
                    <td style={{ padding:'10px 14px' }}>
                      <Badge label={w.status==='active'?'Actif':'Inactif'} color={w.status==='active'?D.green:D.amber} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Ligne bas : Alertes + Agents */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
        <div style={card}>
          <div style={{ padding:'12px 16px', borderBottom:`1px solid ${D.b1}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx }}>Alertes actives</div>
            {alertes.length > 0 && (
              <button onClick={() => setActive('alertes')} style={{ background:'transparent', border:'none', color:D.tx3, fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Voir tout →</button>
            )}
          </div>
          {alertes.length === 0 ? (
            <div style={{ padding:'1.5rem', textAlign:'center', color:D.tx3, fontSize:'11px' }}>Aucune alerte active ✓</div>
          ) : (
            alertes.slice(0, 4).map((a,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 16px', borderBottom:i<Math.min(alertes.length,4)-1?`1px solid ${D.b1}`:'none' }}>
                <div style={{ width:'6px', height:'6px', borderRadius:'50%', flexShrink:0, background:a.level==='crit'?D.red:a.level==='warn'?D.amber:D.blue }} />
                <div style={{ flex:1, fontSize:'11px', color:D.tx2 }}>{a.titre}</div>
                <div style={{ fontSize:'10px', color:D.tx3, whiteSpace:'nowrap' }}>{timeAgo(a.created_at)}</div>
              </div>
            ))
          )}
        </div>
        <div style={card}>
          <div style={{ padding:'12px 16px', borderBottom:`1px solid ${D.b1}` }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx }}>Agents IA — Activité globale</div>
          </div>
          <div style={{ padding:'1.5rem', textAlign:'center', color:D.tx3, fontSize:'11px' }}>
            Les métriques d'activité des agents seront branchées à la prochaine étape.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CLIENTS ─────────────────────────────────────────────────────────────────
function Clients() {
  const [workspaces, setWorkspaces] = useState([]);
  const [selected, setSelected]     = useState(null);
  const [newPlan, setNewPlan]        = useState('');
  const [creditMontant, setCreditMontant] = useState('');
  const [saving, setSaving]          = useState(false);
  const [showCreate, setShowCreate]  = useState(false);
  const [newClient, setNewClient]    = useState({ name:'', email:'', plan:'starter', siren:'' });
  const [creatingClient, setCreatingClient] = useState(false);
  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.email) { window.alert('Nom du workspace et email admin requis'); return; }
    setCreatingClient(true);
    try {
      const r = await api.post('/api/admin/workspaces', {
        name: newClient.name, email: newClient.email,
        siren: newClient.siren || null, plan: newClient.plan || 'starter',
      });
      setShowCreate(false);
      setNewClient({ name:'', email:'', plan:'starter', siren:'' });
      const list = await api.get('/api/admin/workspaces');
      setWorkspaces(list.data || []);
      const tmp = r.data && r.data.temp_password;
      if (tmp) window.alert(`Client créé \u2713\n\nAccès admin : ${r.data.admin_email}\nMot de passe temporaire : ${tmp}\n\nÀ communiquer au client (modifiable ensuite).`);
    } catch (e) {
      window.alert((e.response && e.response.data && e.response.data.detail) || 'Erreur à la création du client');
    } finally { setCreatingClient(false); }
  };
  const [toast, setToast]            = useState(null);

  useEffect(() => {
    api.get('/api/admin/workspaces').then(r => setWorkspaces(r.data||[])).catch(()=>{});
  }, []);

  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  const handlePlanChange = async () => {
    if (!newPlan || !selected) return;
    setSaving(true);
    try {
      await api.patch(`/api/admin/workspaces/${selected.id}/plan`, { plan: newPlan });
      setWorkspaces(ws => ws.map(w => w.id===selected.id ? {...w,plan:newPlan} : w));
      setSelected(s => ({...s,plan:newPlan}));
      showToast(`Plan changé en ${newPlan} ✓`);
    } catch { showToast('Erreur','error'); }
    finally { setSaving(false); }
  };

  const handleCredit = async () => {
    if (!creditMontant || !selected) return;
    setSaving(true);
    try {
      await api.post('/api/wallet/credit', null, { params: { workspace_id:selected.id, montant:Number(creditMontant), reference:`ADMIN-${Date.now()}` }});
      showToast(`${creditMontant} € crédités ✓`);
      setCreditMontant('');
    } catch { showToast('Erreur','error'); }
    finally { setSaving(false); }
  };

  const handleActivate = async () => {
    try {
      await api.patch(`/api/admin/workspaces/${selected.id}/activate`);
      showToast('Workspace activé ✓');
    } catch { showToast('Erreur','error'); }
  };

  const [configData, setConfigData] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);

  // CSV Import state
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  // Airtable wizard state
  const [atStep, setAtStep] = useState(-1); // -1=fermé, 0-4=étapes
  const [atConfig, setAtConfig] = useState({ api_key:'', base_id:'', table_id:'', field_mapping:{}, status_mapping:{}, attachments_mapping:{}, sync_direction:'bidirectional', create_new_dossiers:true });
  const [atTables, setAtTables] = useState([]);
  const [atColumns, setAtColumns] = useState([]);
  const [atTestOk, setAtTestOk] = useState(false);
  const [atLoading, setAtLoading] = useState(false);
  const [atConnector, setAtConnector] = useState(null); // connecteur existant
  const [atError, setAtError] = useState('');

  // Zendesk wizard state
  const [zdStep, setZdStep] = useState(-1);
  const [zdConfig, setZdConfig] = useState({ subdomain:'', email_auth:'', api_token:'', zendesk_filter:{}, agents_mode:'individual', formaliste_mapping:{}, signature_template:"Cordialement,\n{{formaliste_prenom}} {{formaliste_nom_initiale}}\n{{signature_suffix}}", signature_suffix:'', white_label_mode:true, field_mapping:{}, status_mapping:{}, sync_direction:'bidirectional', create_dossier_on_new_ticket:false });
  const [zdGroups, setZdGroups] = useState([]);
  const [zdAgents, setZdAgents] = useState([]);
  const [zdForms, setZdForms] = useState([]);
  const [zdTestOk, setZdTestOk] = useState(false);
  const [zdLoading, setZdLoading] = useState(false);
  const [zdConnector, setZdConnector] = useState(null);
  const [zdError, setZdError] = useState('');

  // Aircall wizard state
  const [acStep, setAcStep] = useState(-1);
  const [acConfig, setAcConfig] = useState({ api_id:'', api_token:'', webhook_token:'', aircall_filter:{}, enrich_existing_dossiers:true, create_dossier_on_call:false, match_by:['phone','email'] });
  const [acNumbers, setAcNumbers] = useState([]);
  const [acTestOk, setAcTestOk] = useState(false);
  const [acLoading, setAcLoading] = useState(false);
  const [acConnector, setAcConnector] = useState(null);
  const [acError, setAcError] = useState('');

  // Import modal state
  const [importModal, setImportModal] = useState(null); // { type: 'zendesk'|'aircall', connectorId }
  const [importFrom, setImportFrom] = useState('');
  const [importTo, setImportTo] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importDryRun, setImportDryRun] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { status, total_fetched, matched_count, orphans_count }
  const [importJobs, setImportJobs] = useState([]);

  // Charger la config quand un client est sélectionné
  useEffect(() => {
    if (selected) {
      setConfigLoading(true);
      api.get(`/api/admin/workspaces/${selected.id}/config`)
        .then(r => { setConfigData(r.data); setConfigLoading(false); })
        .catch(() => { setConfigData(null); setConfigLoading(false); });
      // Charger le connecteur Airtable existant
      api.get(`/api/admin/workspaces/${selected.id}/connectors/airtable`)
        .then(r => {
          setAtConnector(r.data);
          // Hydrate atConfig from existing connector (reconfigure flow)
          if (r.data && r.data.id) {
            const c = r.data.config || {};
            setAtConfig({
              api_key: '',  // never returned by API; only set if user types a new one
              base_id: c.base_id || '',
              table_id: c.table_id || '',
              field_mapping: c.field_mapping || {},
              status_mapping: c.status_mapping || {},
              value_mappings: c.value_mappings || {},
              attachments_mapping: c.attachments_mapping || {},
              sync_direction: c.sync_direction || 'bidirectional',
              create_new_dossiers: c.create_new_dossiers !== false,
            });
          }
        })
        .catch(() => { setAtConnector(null); });
      // Charger le connecteur Zendesk existant
      api.get(`/api/admin/workspaces/${selected.id}/connectors/zendesk`)
        .then(r => { setZdConnector(r.data); })
        .catch(() => { setZdConnector(null); });
      // Charger le connecteur Aircall existant
      api.get(`/api/admin/workspaces/${selected.id}/connectors/aircall`)
        .then(r => { setAcConnector(r.data); })
        .catch(() => { setAcConnector(null); });
      // Charger les import jobs
      api.get(`/api/admin/workspaces/${selected.id}/import-jobs`)
        .then(r => { setImportJobs(r.data || []); })
        .catch(() => { setImportJobs([]); });
    }
  }, [selected]);

  const handleConfigSave = async () => {
    if (!selected || !configData) return;
    setSaving(true);
    try {
      await api.patch(`/api/admin/workspaces/${selected.id}/config`, configData);
      setWorkspaces(ws => ws.map(w => w.id===selected.id ? {...w, plan:configData.plan, forfait_mensuel:configData.forfait_mensuel, dossiers_inclus:configData.dossiers_inclus, prix_dossier_sup:configData.prix_dossier_sup} : w));
      setSelected(s => ({...s, plan:configData.plan}));
      showToast('Configuration sauvegardée ✓');
    } catch { showToast('Erreur','error'); }
    finally { setSaving(false); }
  };

  const PLAN_COLOR = { enterprise:D.or, pro:D.blue, starter:D.tx3 };
  const inp = { width:'100%', padding:'7px 11px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'7px', fontSize:'12px', color:D.tx, outline:'none', fontFamily:'DM Sans, sans-serif' };
  const fieldLabel = { fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.05em', color:D.tx3, marginBottom:'4px' };

  if (selected) return (
    <div style={{ padding:'1.25rem 1.5rem', flex:1 }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'1.25rem' }}>
        <button onClick={() => { setSelected(null); setConfigData(null); }} style={{ background:'transparent', border:`1px solid ${D.b1}`, color:D.tx2, padding:'5px 12px', fontSize:'11px', borderRadius:'6px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>← Retour</button>
        <div style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:600, color:D.tx }}>{selected.name}</div>
        <Badge label={selected.plan} color={PLAN_COLOR[selected.plan]||D.tx3} />
        <Badge label={selected.status==='active'?'Actif':'Inactif'} color={selected.status==='active'?D.green:D.amber} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'1rem' }}>
        {[
          { l:'Plan actuel',     v:selected.plan,                                       c:PLAN_COLOR[selected.plan] },
          { l:'Wallet INPI',    v:`${Number(selected.wallet_balance||0).toLocaleString('fr-FR')} €`, c:Number(selected.wallet_balance||0)<3000?D.red:D.green },
          { l:'Forfait mensuel', v:`${Number(selected.forfait_mensuel||0).toLocaleString('fr-FR')} €`, c:D.tx },
        ].map(k => (
          <div key={k.l} style={{ ...card, padding:'12px 16px' }}>
            <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:D.tx3, marginBottom:'4px' }}>{k.l}</div>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'20px', fontWeight:700, color:k.c }}>{k.v}</div>
          </div>
        ))}
      </div>

      {/* Config client */}
      {configLoading ? (
        <div style={{ ...card, padding:'2rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>Chargement de la configuration…</div>
      ) : configData ? (
        <div style={{ ...card, padding:'1rem', marginBottom:'1rem' }}>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx, marginBottom:'16px' }}>Configuration du client</div>

          {/* Section Abonnement */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
            <div style={{ fontSize:'11px', fontWeight:600, color:D.or, textTransform:'uppercase', letterSpacing:'0.05em' }}>Abonnement</div>
            <div style={{ display:'flex', gap:'6px' }}>
              <button
                onClick={() => setConfigData({
                  ...configData,
                  plan: 'starter',
                  forfait_mensuel: 500,
                  dossiers_inclus: 10,
                  prix_dossier_sup: 40,
                })}
                style={{
                  background:'transparent', border:`1px solid ${D.or}80`, color:D.or,
                  padding:'4px 10px', borderRadius:'4px', fontSize:'10px', fontWeight:500,
                  cursor:'pointer', fontFamily:'DM Sans, sans-serif', textTransform:'uppercase', letterSpacing:'0.05em',
                }}
                title="Pré-remplit : Starter / 500€ / 10 dossiers / 40€ par dossier sup."
              >
                Preset Starter 500€
              </button>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr', gap:'10px', marginBottom:'16px' }}>
            <div>
              <div style={fieldLabel}>Service</div>
              <select style={inp} value={configData.service_model||'plateforme_only'} onChange={e => setConfigData({...configData, service_model:e.target.value})}>
                <option value="plateforme_only">Plateforme seule</option>
                <option value="plateforme_lexback">Plateforme + Équipe Lexback</option>
              </select>
            </div>
            <div>
              <div style={fieldLabel}>Plan</div>
              <select style={inp} value={configData.plan||''} onChange={e => setConfigData({...configData, plan:e.target.value})}>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <div style={fieldLabel}>Forfait mensuel (EUR)</div>
              <input style={inp} type="number" value={configData.forfait_mensuel||''} onChange={e => setConfigData({...configData, forfait_mensuel:e.target.value?Number(e.target.value):null})} placeholder="0" />
            </div>
            <div>
              <div style={fieldLabel}>Dossiers inclus</div>
              <input style={inp} type="number" value={configData.dossiers_inclus||''} onChange={e => setConfigData({...configData, dossiers_inclus:e.target.value?Number(e.target.value):null})} placeholder="20" />
            </div>
            <div>
              <div style={fieldLabel}>Prix dossier sup. (EUR)</div>
              <input style={inp} type="number" value={configData.prix_dossier_sup||''} onChange={e => setConfigData({...configData, prix_dossier_sup:e.target.value?Number(e.target.value):null})} placeholder="35" />
            </div>
          </div>

          {/* Section Wallet */}
          <div style={{ fontSize:'11px', fontWeight:600, color:D.or, marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Wallet</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'16px' }}>
            <div>
              <div style={fieldLabel}>Seuil alerte wallet (EUR)</div>
              <input style={inp} type="number" value={configData.wallet_alert_threshold||''} onChange={e => setConfigData({...configData, wallet_alert_threshold:e.target.value?Number(e.target.value):null})} placeholder="3000" />
            </div>
            <div>
              <div style={fieldLabel}>Pause auto si wallet vide</div>
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'4px' }}>
                <button
                  onClick={() => setConfigData({...configData, wallet_auto_pause:!configData.wallet_auto_pause})}
                  style={{
                    width:'36px', height:'20px', borderRadius:'10px', border:'none', cursor:'pointer',
                    background: configData.wallet_auto_pause ? D.green : D.s2,
                    position:'relative', transition:'background 0.2s',
                  }}
                >
                  <div style={{
                    width:'16px', height:'16px', borderRadius:'50%', background:'#fff',
                    position:'absolute', top:'2px', left:configData.wallet_auto_pause?'18px':'2px',
                    transition:'left 0.2s',
                  }} />
                </button>
                <span style={{ fontSize:'11px', color:D.tx2 }}>{configData.wallet_auto_pause ? 'Activée' : 'Désactivée'}</span>
              </div>
            </div>
          </div>

          {/* Section SLA */}
          <div style={{ fontSize:'11px', fontWeight:600, color:D.or, marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.05em' }}>SLA</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'16px' }}>
            <div>
              <div style={fieldLabel}>SLA standard (heures)</div>
              <input style={inp} type="number" value={configData.standard_sla_h||''} onChange={e => setConfigData({...configData, standard_sla_h:e.target.value?Number(e.target.value):null})} placeholder="48" />
            </div>
            <div>
              <div style={fieldLabel}>SLA urgent (heures)</div>
              <input style={inp} type="number" value={configData.priorite_sla_h||''} onChange={e => setConfigData({...configData, priorite_sla_h:e.target.value?Number(e.target.value):null})} placeholder="24" />
            </div>
          </div>

          <button onClick={handleConfigSave} disabled={saving} style={{ background:D.or, color:'#09090E', border:'none', padding:'8px 20px', borderRadius:'6px', fontSize:'12px', fontWeight:500, cursor:'pointer', opacity:saving?0.6:1, fontFamily:'DM Sans, sans-serif' }}>
            {saving ? 'Enregistrement…' : 'Enregistrer la configuration'}
          </button>
        </div>
      ) : null}

      {/* ─── Intégrations Airtable ─── */}
      <div style={{ ...card, padding:'1rem', marginBottom:'1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: atStep >= 0 ? '16px' : 0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx }}>Airtable</div>
            {atConnector?.is_active ? (
              <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:D.green, background:'rgba(46,204,138,0.12)' }}>Connecté</span>
            ) : (
              <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:D.tx3, background:D.s2 }}>Non configuré</span>
            )}
            {atConnector?.is_active && (() => {
              const whStatus = atConnector?.config?.airtable_webhook_status;
              const whId = atConnector?.config?.airtable_webhook_id;
              if (whId) {
                const colors = { active: D.green, expiring_soon: D.or, expired: D.red };
                const labels = { active: 'Webhook auto', expiring_soon: 'Webhook expire bientôt', expired: 'Webhook expiré' };
                return <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:colors[whStatus] || D.green, background:`${colors[whStatus] || D.green}20` }}>{labels[whStatus] || 'Webhook auto'}</span>;
              }
              return <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:D.tx3, background:D.s2 }}>Webhook manuel</span>;
            })()}
          </div>
          <div style={{ display:'flex', gap:'6px' }}>
            {atConnector?.is_active && (
              <>
                <button onClick={() => { setImportModal({ type:'airtable', connectorId:atConnector.id }); setImportFrom(''); setImportTo(''); setImportDryRun(false); }} style={{ background:'rgba(91,141,239,0.1)', color:D.blue, border:`1px solid rgba(91,141,239,0.3)`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                  Importer historique
                </button>
                <button onClick={async () => { try { await api.delete(`/api/admin/workspaces/${selected.id}/connectors/airtable`); setAtConnector(null); setAtStep(-1); setAtConfig({ api_key:'', base_id:'', table_id:'', field_mapping:{}, status_mapping:{}, attachments_mapping:{}, sync_direction:'bidirectional', create_new_dossiers:true }); showToast('Airtable déconnecté'); } catch { showToast('Erreur','error'); } }} style={{ background:'rgba(239,91,91,0.1)', color:D.red, border:`1px solid rgba(239,91,91,0.3)`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                  Déconnecter
                </button>
              </>
            )}
            <button onClick={async () => {
              const opening = atStep < 0;
              setAtStep(opening ? 0 : -1);
              setAtError('');
              if (opening && atConnector?.is_active) {
                // Reconfigure flow: mark connection as already tested and load schema using stored PAT
                setAtTestOk(true);
                try {
                  const r = await api.post(`/api/admin/workspaces/${selected.id}/connectors/airtable/schema`);
                  if (r.data?.success) {
                    const tbls = r.data.tables || [];
                    setAtTables(tbls);
                    const sel = tbls.find(t => t.id === (atConnector.config?.table_id || '') || t.name === (atConnector.config?.table_id || ''));
                    if (sel) setAtColumns(sel.fields || []);
                  }
                } catch { /* silent; user can still re-test manually via Modifier */ }
              } else {
                setAtTestOk(false);
              }
            }} style={{ background:atStep >= 0 ? D.s2 : 'rgba(212,168,67,0.12)', color:atStep >= 0 ? D.tx2 : D.or, border:`1px solid ${atStep >= 0 ? D.b1 : 'rgba(212,168,67,0.3)'}`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
              {atStep >= 0 ? 'Fermer' : atConnector?.is_active ? 'Reconfigurer' : 'Configurer'}
            </button>
          </div>
        </div>

        {atStep >= 0 && (
          <div>
            {/* Stepper */}
            <div style={{ display:'flex', gap:'4px', marginBottom:'16px' }}>
              {['Connexion','Mapping champs','Mapping statuts','Pièces jointes','Activation'].map((label, i) => (
                <div key={i} style={{ flex:1, textAlign:'center' }}>
                  <div style={{ height:'3px', borderRadius:'2px', background: i <= atStep ? D.or : D.b1, marginBottom:'4px', transition:'background 0.2s' }} />
                  <span style={{ fontSize:'9px', color: i <= atStep ? D.or : D.tx3 }}>{label}</span>
                </div>
              ))}
            </div>

            {atError && <div style={{ background:'rgba(239,91,91,0.1)', border:`1px solid rgba(239,91,91,0.3)`, borderRadius:'6px', padding:'8px 12px', marginBottom:'12px', fontSize:'11px', color:D.red }}>{atError}</div>}

            {/* Étape 0 — Connexion */}
            {atStep === 0 && (
              <div>
                {atConnector?.is_active && !atConfig.api_key ? (
                  <>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                      <div>
                        <div style={fieldLabel}>Clé API Airtable (Personal Access Token)</div>
                        <div style={{ ...inp, display:'flex', alignItems:'center', justifyContent:'space-between', color:D.tx3, fontFamily:'monospace', cursor:'default' }}>
                          <span>pat••••••••••••••••</span>
                          <button onClick={() => { setAtConfig(c => ({...c, api_key:' '})); setAtTestOk(false); }} style={{ background:'none', border:'none', color:D.or, fontSize:'10px', cursor:'pointer', textDecoration:'underline', fontFamily:'DM Sans, sans-serif' }}>Modifier</button>
                        </div>
                      </div>
                      <div>
                        <div style={fieldLabel}>Base ID</div>
                        <div style={{ ...inp, color:D.tx2, fontFamily:'monospace', cursor:'default' }}>{atConfig.base_id || '—'}</div>
                      </div>
                    </div>
                    <div style={{ fontSize:'11px', color:D.green, marginBottom:'12px' }}>Connexion existante — passez directement aux étapes suivantes</div>
                    {atTables.length > 0 && (
                      <div style={{ marginBottom:'12px' }}>
                        <div style={fieldLabel}>Table cible</div>
                        <select style={inp} value={atConfig.table_id} onChange={e => {
                          const tid = e.target.value;
                          setAtConfig(c => ({...c, table_id:tid}));
                          const t = atTables.find(t => t.id === tid || t.name === tid);
                          setAtColumns(t ? t.fields : []);
                        }}>
                          {atTables.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                    )}
                    <div style={{ textAlign:'right' }}>
                      <button onClick={() => setAtStep(1)} disabled={!atConfig.table_id} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', opacity: !atConfig.table_id ? 0.5 : 1, fontFamily:'DM Sans, sans-serif' }}>
                        Suivant
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                  <div>
                    <div style={fieldLabel}>Clé API Airtable (Personal Access Token)</div>
                    <input style={inp} type="password" placeholder="pat..." value={atConfig.api_key.trim()} onChange={e => setAtConfig({...atConfig, api_key:e.target.value})} />
                  </div>
                  <div>
                    <div style={fieldLabel}>Base ID</div>
                    <input style={inp} placeholder="appXXXXXXXXXXXXXX" value={atConfig.base_id} onChange={e => setAtConfig({...atConfig, base_id:e.target.value})} />
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <button
                    disabled={!atConfig.api_key.trim() || !atConfig.base_id || atLoading}
                    onClick={async () => {
                      setAtLoading(true); setAtError('');
                      try {
                        const r = await api.post(`/api/admin/workspaces/${selected.id}/connectors/airtable/test`, { api_key: atConfig.api_key.trim(), base_id: atConfig.base_id });
                        if (r.data.success) {
                          setAtTables(r.data.tables || []);
                          setAtTestOk(true);
                          if (r.data.tables?.length === 1) {
                            setAtConfig(c => ({...c, table_id: r.data.tables[0].id}));
                            setAtColumns(r.data.tables[0].fields || []);
                          }
                        } else { setAtError(r.data.error || 'Erreur de connexion'); setAtTestOk(false); }
                      } catch(e) { setAtError(e.response?.data?.detail || 'Erreur réseau'); setAtTestOk(false); }
                      finally { setAtLoading(false); }
                    }}
                    style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', opacity:!atConfig.api_key.trim()||!atConfig.base_id||atLoading?0.5:1, fontFamily:'DM Sans, sans-serif' }}
                  >
                    {atLoading ? 'Test en cours...' : 'Tester la connexion'}
                  </button>
                  {atTestOk && <span style={{ fontSize:'11px', color:D.green }}>Connexion OK</span>}
                  {atConnector?.is_active && <button onClick={() => { setAtConfig(c => ({...c, api_key:''})); setAtTestOk(true); }} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'6px 12px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Annuler</button>}
                </div>
                {atTestOk && atTables.length > 0 && (
                  <div style={{ marginTop:'12px' }}>
                    <div style={fieldLabel}>Table cible</div>
                    <select style={inp} value={atConfig.table_id} onChange={e => {
                      const tid = e.target.value;
                      setAtConfig(c => ({...c, table_id:tid}));
                      const t = atTables.find(t => t.id === tid);
                      setAtColumns(t ? t.fields : []);
                    }}>
                      <option value="">Sélectionner une table...</option>
                      {atTables.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                {atTestOk && atConfig.table_id && (
                  <div style={{ marginTop:'12px', textAlign:'right' }}>
                    <button onClick={() => setAtStep(1)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                      Suivant
                    </button>
                  </div>
                )}
                  </>
                )}
              </div>
            )}

            {/* Étape 1 — Mapping champs enrichi */}
            {atStep === 1 && (() => {
              const FIELD_SECTIONS = [
                { title:'Identité', fields:[
                  { key:'societe_nom', label:'Nom société', aliases:['nom','denomination','societe','raison sociale','company'] },
                  { key:'societe_siren', label:'SIREN', aliases:['siren','siret','rcs'] },
                  { key:'reference', label:'Référence', aliases:['ref','reference','numero'] },
                ]},
                { title:'Formalité', fields:[
                  { key:'type', label:'Type formalité', aliases:['type','formalite','nature'] },
                  { key:'status', label:'Statut', aliases:['statut','status','etat','état'] },
                  { key:'priorite', label:'Priorité', aliases:['priorite','priorité','urgence'] },
                ]},
                { title:'Intervenants', fields:[
                  { key:'formaliste_airtable', label:'Formaliste', aliases:['formaliste','assignee','responsable','charge'] },
                  { key:'btob_cabinet', label:'Cabinet BtoB', aliases:['btob','cabinet','partenaire','apporteur'] },
                  { key:'greffe', label:'Greffe', aliases:['greffe','tribunal'] },
                  { key:'commercial', label:'Commercial', aliases:['commercial','vendeur','sales'] },
                ]},
                { title:'Contact client', fields:[
                  { key:'email_client_final', label:'Email client', aliases:['email','mail','courriel','email client'] },
                  { key:'telephone_client_final', label:'Téléphone', aliases:['telephone','téléphone','tel','phone','mobile'] },
                ]},
                { title:'Suivi', fields:[
                  { key:'elements_manquants', label:'Éléments manquants', aliases:['manquant','elements manquants','missing','a fournir'] },
                  { key:'etat_facturation', label:'État facturation', aliases:['facturation','facture','billing','paiement'] },
                  { key:'url_facture', label:'URL facture', aliases:['url facture','lien facture'] },
                ]},
                { title:'Dates', fields:[
                  { key:'date_creation_airtable', label:'Date création AT', aliases:['date creation','created','cree le','créé'] },
                  { key:'date_modification_airtable', label:'Date modif AT', aliases:['date modification','modified','modifie le','modifié'] },
                ]},
                { title:'Lexback interne', fields:[
                  { key:'debours_lexback', label:'Débours', aliases:['debours','débours','frais'] },
                  { key:'notes_formaliste_lexback', label:'Notes formaliste', aliases:['notes','commentaire','remarque'] },
                  { key:'score_qualite_lexback', label:'Score qualité', aliases:['score','qualite','qualité'] },
                ]},
              ];
              const normalize = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9]/g,'');
              const getFieldVal = (key) => {
                const v = atConfig.field_mapping[key];
                if (!v) return '';
                if (typeof v === 'string') return v;
                return v.airtable_col || '';
              };
              const getFieldMode = (key) => {
                const v = atConfig.field_mapping[key];
                if (!v || typeof v === 'string') return 'bidirectional';
                return v.mode || 'bidirectional';
              };
              const setField = (key, col, mode) => {
                setAtConfig(c => ({...c, field_mapping:{...c.field_mapping, [key]:{ airtable_col:col, mode:mode||getFieldMode(key) }}}));
              };
              const setMode = (key, mode) => {
                const col = getFieldVal(key);
                setAtConfig(c => ({...c, field_mapping:{...c.field_mapping, [key]:{ airtable_col:col, mode }}}));
              };
              const autoDetect = () => {
                const auto = {};
                FIELD_SECTIONS.forEach(sec => sec.fields.forEach(f => {
                  const aliases = [f.key.replace(/_/g,' '), ...f.aliases];
                  const match = atColumns.find(c => {
                    const cn = normalize(c.name);
                    return aliases.some(a => cn.includes(normalize(a)) || normalize(a).includes(cn));
                  });
                  if (match) auto[f.key] = { airtable_col:match.name, mode: sec.title === 'Lexback interne' ? 'lexback_only' : 'bidirectional' };
                }));
                setAtConfig(c => ({...c, field_mapping:{...c.field_mapping, ...auto}}));
              };
              const totalFields = FIELD_SECTIONS.reduce((n,s) => n+s.fields.length, 0);
              const mappedCount = FIELD_SECTIONS.reduce((n,s) => n+s.fields.filter(f => getFieldVal(f.key)).length, 0);
              const modeLabels = { bidirectional:'Bidir', read_only:'Lecture', lexback_only:'Lexback' };
              const modeColors = { bidirectional:D.green, read_only:D.blue, lexback_only:D.or };
              return (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                    <div style={{ fontSize:'11px', color:D.tx2 }}>Associez chaque champ Lexback à une colonne Airtable. <span style={{ color:D.or }}>{mappedCount}/{totalFields}</span></div>
                    <button onClick={autoDetect} style={{ background:'transparent', color:D.blue, border:`1px solid rgba(91,141,239,0.3)`, padding:'4px 10px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                      Auto-détecter
                    </button>
                  </div>
                  {FIELD_SECTIONS.map(sec => (
                    <div key={sec.title} style={{ marginBottom:'12px' }}>
                      <div style={{ fontSize:'10px', fontWeight:600, color:D.or, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.05em' }}>{sec.title}</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                        {sec.fields.map(f => (
                          <div key={f.key} style={{ display:'flex', gap:'4px', alignItems:'flex-end' }}>
                            <div style={{ flex:1 }}>
                              <div style={fieldLabel}>{f.label}</div>
                              <select style={{...inp, fontSize:'10px'}} value={getFieldVal(f.key)} onChange={e => setField(f.key, e.target.value)}>
                                <option value="">— Non mappé —</option>
                                {atColumns.map(col => <option key={col.id||col.name} value={col.name}>{col.name} ({col.type})</option>)}
                              </select>
                            </div>
                            {getFieldVal(f.key) && (
                              <select style={{...inp, width:'80px', fontSize:'9px', padding:'5px 4px', color:modeColors[getFieldMode(f.key)]}} value={getFieldMode(f.key)} onChange={e => setMode(f.key, e.target.value)}>
                                <option value="bidirectional">Bidir</option>
                                <option value="read_only">Lecture</option>
                                <option value="lexback_only">Lexback</option>
                              </select>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <button onClick={() => setAtStep(0)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 16px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Retour</button>
                    <button onClick={() => setAtStep(2)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Suivant</button>
                  </div>
                </div>
              );
            })()}

            {/* Étape 2 — Mapping statuts + options */}
            {atStep === 2 && (
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'12px' }}>Indiquez le nom du statut dans Airtable pour chaque statut Lexback.</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'16px' }}>
                  {[
                    { key:'recu',         label:'Reçu' },
                    { key:'assigne',      label:'Assigné' },
                    { key:'en_cours',     label:'En cours' },
                    { key:'depose',       label:'Déposé' },
                    { key:'termine',      label:'Terminé' },
                    { key:'rejete',       label:'Rejeté' },
                    { key:'doc_manquant', label:'Doc manquant' },
                  ].map(s => (
                    <div key={s.key}>
                      <div style={fieldLabel}>{s.label}</div>
                      <input style={inp} placeholder={s.key} value={atConfig.status_mapping[s.key]||''} onChange={e => setAtConfig(c => ({...c, status_mapping:{...c.status_mapping, [s.key]:e.target.value}}))} />
                    </div>
                  ))}
                </div>
                <div style={{ fontSize:'11px', fontWeight:600, color:D.or, marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Options</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'16px' }}>
                  <div>
                    <div style={fieldLabel}>Direction de sync</div>
                    <select style={inp} value={atConfig.sync_direction} onChange={e => setAtConfig(c => ({...c, sync_direction:e.target.value}))}>
                      <option value="bidirectional">Bidirectionnelle</option>
                      <option value="outbound_only">Lexback vers Airtable</option>
                      <option value="inbound_only">Airtable vers Lexback</option>
                    </select>
                  </div>
                  <div>
                    <div style={fieldLabel}>Créer les nouveaux dossiers depuis Airtable</div>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'4px' }}>
                      <button
                        onClick={() => setAtConfig(c => ({...c, create_new_dossiers:!c.create_new_dossiers}))}
                        style={{
                          width:'36px', height:'20px', borderRadius:'10px', border:'none', cursor:'pointer',
                          background: atConfig.create_new_dossiers ? D.green : D.s2,
                          position:'relative', transition:'background 0.2s',
                        }}
                      >
                        <div style={{
                          width:'16px', height:'16px', borderRadius:'50%', background:'#fff',
                          position:'absolute', top:'2px', left:atConfig.create_new_dossiers?'18px':'2px',
                          transition:'left 0.2s',
                        }} />
                      </button>
                      <span style={{ fontSize:'11px', color:D.tx2 }}>{atConfig.create_new_dossiers ? 'Oui' : 'Non'}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <button onClick={() => setAtStep(1)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 16px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Retour</button>
                  <button onClick={() => setAtStep(3)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Suivant</button>
                </div>
              </div>
            )}

            {/* Étape 3 — Pièces jointes */}
            {atStep === 3 && (() => {
              const attachCols = atColumns.filter(c => c.type === 'multipleAttachments');
              const categories = [
                { value:'justificative', label:'Justificative' },
                { value:'juridique', label:'Juridique' },
                { value:'officiel', label:'Officiel' },
                { value:'lexback_internal', label:'Lexback interne' },
              ];
              return (
                <div>
                  <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'12px' }}>Mappez les colonnes Airtable contenant des pièces jointes vers des catégories Lexback.</div>
                  {attachCols.length === 0 ? (
                    <div style={{ background:D.s2, borderRadius:'6px', padding:'12px', fontSize:'11px', color:D.tx3, marginBottom:'16px' }}>Aucune colonne de type "Attachments" détectée dans cette table.</div>
                  ) : (
                    <div style={{ display:'grid', gap:'8px', marginBottom:'16px' }}>
                      {attachCols.map(col => {
                        const key = col.name;
                        const current = atConfig.attachments_mapping[key] || {};
                        return (
                          <div key={key} style={{ display:'flex', gap:'8px', alignItems:'center', background:D.s2, borderRadius:'6px', padding:'8px 12px' }}>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:'11px', fontWeight:500, color:D.tx }}>{col.name}</div>
                              <div style={{ fontSize:'9px', color:D.tx3 }}>multipleAttachments</div>
                            </div>
                            <select style={{...inp, width:'140px', fontSize:'10px'}} value={current.category || ''} onChange={e => {
                              setAtConfig(c => ({
                                ...c, attachments_mapping:{...c.attachments_mapping, [key]:{ airtable_col:key, category:e.target.value }}
                              }));
                            }}>
                              <option value="">— Ignorer —</option>
                              {categories.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <button onClick={() => setAtStep(2)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 16px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Retour</button>
                    <button onClick={() => setAtStep(4)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Suivant</button>
                  </div>
                </div>
              );
            })()}

            {/* Étape 4 — Activation */}
            {atStep === 4 && (() => {
              const mappedCount = Object.values(atConfig.field_mapping).filter(v => (typeof v === 'string' ? v : v?.airtable_col)).length;
              const attachCount = Object.values(atConfig.attachments_mapping).filter(v => v?.category).length;
              return (
                <div>
                  <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'12px' }}>Vérifiez la configuration et activez la synchronisation.</div>
                  <div style={{ background:D.s2, borderRadius:'7px', padding:'12px', marginBottom:'12px' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', fontSize:'11px' }}>
                      <div><span style={{ color:D.tx3 }}>Base ID:</span> <span style={{ color:D.tx }}>{atConfig.base_id}</span></div>
                      <div><span style={{ color:D.tx3 }}>Table:</span> <span style={{ color:D.tx }}>{atTables.find(t=>t.id===atConfig.table_id)?.name || atConfig.table_id}</span></div>
                      <div><span style={{ color:D.tx3 }}>Direction:</span> <span style={{ color:D.tx }}>{{bidirectional:'Bidirectionnelle',outbound_only:'Sortante seule',inbound_only:'Entrante seule'}[atConfig.sync_direction]}</span></div>
                      <div><span style={{ color:D.tx3 }}>Champs mappés:</span> <span style={{ color:D.tx }}>{mappedCount}</span></div>
                      {attachCount > 0 && <div><span style={{ color:D.tx3 }}>PJ mappées:</span> <span style={{ color:D.tx }}>{attachCount} colonne(s)</span></div>}
                    </div>
                  </div>
                  {/* Webhook section: auto or manual */}
                  {atConnector?.config?.airtable_webhook_id ? (
                    <div style={{ marginBottom:'12px', background:'rgba(46,204,138,0.06)', border:`1px solid rgba(46,204,138,0.2)`, borderRadius:'6px', padding:'10px 12px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div>
                          <div style={{ fontSize:'11px', fontWeight:600, color:D.green, marginBottom:'2px' }}>Webhook automatique actif</div>
                          <div style={{ fontSize:'10px', color:D.tx3 }}>
                            {atConnector.config.airtable_webhook_expires_at ? `Expire le ${new Date(atConnector.config.airtable_webhook_expires_at).toLocaleDateString('fr-FR')}` : 'Actif'}
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await api.post(`/api/admin/workspaces/${selected.id}/connectors/airtable/refresh-webhook`);
                              showToast('Webhook rafraîchi');
                              const r = await api.get(`/api/admin/workspaces/${selected.id}/connectors/airtable`);
                              setAtConnector(r.data);
                            } catch(e) { showToast(e.response?.data?.detail || 'Erreur refresh', 'error'); }
                          }}
                          style={{ background:'rgba(46,204,138,0.12)', color:D.green, border:`1px solid rgba(46,204,138,0.3)`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}
                        >Rafraîchir</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {atConnector?.webhook_url && (
                        <div style={{ marginBottom:'12px' }}>
                          <div style={fieldLabel}>URL Webhook (à configurer dans Airtable Automations)</div>
                          <div style={{ display:'flex', gap:'6px' }}>
                            <input style={{...inp, flex:1}} readOnly value={atConnector.webhook_url} />
                            <button onClick={() => { navigator.clipboard.writeText(atConnector.webhook_url); showToast('URL copiée'); }} style={{ background:D.s2, color:D.tx2, border:`1px solid ${D.b1}`, padding:'5px 10px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Copier</button>
                          </div>
                        </div>
                      )}
                      {atConnector?.webhook_secret && (
                        <div style={{ marginBottom:'12px' }}>
                          <div style={fieldLabel}>Webhook Secret (pour la signature HMAC)</div>
                          <div style={{ display:'flex', gap:'6px' }}>
                            <input style={{...inp, flex:1}} readOnly value={atConnector.webhook_secret} />
                            <button onClick={() => { navigator.clipboard.writeText(atConnector.webhook_secret); showToast('Secret copié'); }} style={{ background:D.s2, color:D.tx2, border:`1px solid ${D.b1}`, padding:'5px 10px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Copier</button>
                          </div>
                        </div>
                      )}
                      {atConnector?.is_active && !atConnector?.config?.airtable_webhook_id && (
                        <div style={{ marginBottom:'12px' }}>
                          <button
                            onClick={async () => {
                              try {
                                showToast('Configuration du webhook auto...');
                                const r = await api.post(`/api/admin/workspaces/${selected.id}/connectors/airtable/setup-webhook`);
                                if (r.data.success) {
                                  showToast('Webhook auto configuré !');
                                  const cr = await api.get(`/api/admin/workspaces/${selected.id}/connectors/airtable`);
                                  setAtConnector(cr.data);
                                } else {
                                  showToast(r.data.error || 'Erreur setup webhook', 'error');
                                }
                              } catch(e) { showToast(e.response?.data?.detail || 'Erreur setup webhook', 'error'); }
                            }}
                            style={{ background:'rgba(212,168,67,0.12)', color:D.or, border:`1px solid rgba(212,168,67,0.3)`, padding:'6px 14px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif', width:'100%' }}
                          >Configurer webhook automatique (recommandé)</button>
                        </div>
                      )}
                    </>
                  )}
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <button onClick={() => setAtStep(3)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 16px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Retour</button>
                    <button
                      disabled={atLoading}
                      onClick={async () => {
                        setAtLoading(true); setAtError('');
                        try {
                          const payload = { ...atConfig, api_key: atConfig.api_key.trim() };
                          if (!payload.api_key) delete payload.api_key;  // reconfigure: keep stored PAT
                          const r = await api.post(`/api/admin/workspaces/${selected.id}/connectors/airtable`, payload);
                          setAtConnector(r.data);
                          setAtStep(-1);
                          showToast('Airtable connecté et activé');
                          // Auto-setup webhook
                          try {
                            const wh = await api.post(`/api/admin/workspaces/${selected.id}/connectors/airtable/setup-webhook`);
                            if (wh.data.success) {
                              showToast('Webhook auto configuré');
                              const cr = await api.get(`/api/admin/workspaces/${selected.id}/connectors/airtable`);
                              setAtConnector(cr.data);
                            }
                          } catch(whErr) { showToast('Webhook auto non configuré (fallback manuel)', 'warning'); }
                        } catch(e) { setAtError(e.response?.data?.detail || 'Erreur lors de l\'activation'); }
                        finally { setAtLoading(false); }
                      }}
                      style={{ background:D.green, color:'#09090E', border:'none', padding:'7px 20px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', opacity:atLoading?0.5:1, fontFamily:'DM Sans, sans-serif' }}
                    >
                      {atLoading ? 'Activation...' : 'Activer la synchronisation'}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* ─── Imports CSV ─── */}
      <div style={{ ...card, padding:'1rem', marginBottom:'1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: csvImportOpen ? '16px' : 0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx }}>Imports CSV</div>
            <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:D.tx3, background:D.s2 }}>
              Migration de données
            </span>
          </div>
          <button onClick={() => setCsvImportOpen(!csvImportOpen)} style={{
            background: csvImportOpen ? D.s2 : 'rgba(212,168,67,0.12)',
            color: csvImportOpen ? D.tx2 : D.or,
            border:`1px solid ${csvImportOpen ? D.b1 : 'rgba(212,168,67,0.3)'}`,
            padding:'5px 12px', fontSize:'10px', borderRadius:'5px',
            cursor:'pointer', fontFamily:'DM Sans, sans-serif',
          }}>
            {csvImportOpen ? 'Fermer' : 'Importer un CSV'}
          </button>
        </div>
        {!csvImportOpen && (
          <div style={{ fontSize:'11px', color:D.tx3, marginTop:'4px' }}>
            Importer en masse des dossiers depuis un fichier CSV (Airtable export, Excel, etc.)
          </div>
        )}
        {csvImportOpen && (
          <ImportsPage
            initialWorkspaceId={selected.id}
            workspaceName={selected.name}
            embedded={true}
            onClose={() => setCsvImportOpen(false)}
          />
        )}
      </div>

      {/* ─── Intégrations Zendesk ─── */}
      <div style={{ ...card, padding:'1rem', marginBottom:'1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: zdStep >= 0 ? '16px' : 0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx }}>Zendesk</div>
            {zdConnector?.is_active ? (
              <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:D.green, background:'rgba(46,204,138,0.12)' }}>Connecté</span>
            ) : (
              <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:D.tx3, background:D.s2 }}>Non configuré</span>
            )}
          </div>
          <div style={{ display:'flex', gap:'6px' }}>
            {zdConnector?.is_active && (
              <>
                <button onClick={() => { setImportModal({ type:'zendesk', connectorId:zdConnector.id }); setImportFrom(''); setImportTo(''); }} style={{ background:'rgba(91,141,239,0.1)', color:D.blue, border:`1px solid rgba(91,141,239,0.3)`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                  Importer historique
                </button>
                <button onClick={async () => { try { await api.delete(`/api/admin/workspaces/${selected.id}/connectors/zendesk`); setZdConnector(null); setZdStep(-1); showToast('Zendesk déconnecté'); } catch { showToast('Erreur','error'); } }} style={{ background:'rgba(239,91,91,0.1)', color:D.red, border:`1px solid rgba(239,91,91,0.3)`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                  Déconnecter
                </button>
              </>
            )}
            <button onClick={() => { setZdStep(zdStep >= 0 ? -1 : 0); setZdError(''); setZdTestOk(false); }} style={{ background:zdStep >= 0 ? D.s2 : 'rgba(212,168,67,0.12)', color:zdStep >= 0 ? D.tx2 : D.or, border:`1px solid ${zdStep >= 0 ? D.b1 : 'rgba(212,168,67,0.3)'}`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
              {zdStep >= 0 ? 'Fermer' : zdConnector?.is_active ? 'Reconfigurer' : 'Configurer'}
            </button>
          </div>
        </div>

        {zdStep >= 0 && (
          <div>
            {/* Stepper */}
            <div style={{ display:'flex', gap:'4px', marginBottom:'16px' }}>
              {['Connexion','Filtrage','Formalistes','Signature & Activation'].map((label, i) => (
                <div key={i} style={{ flex:1, textAlign:'center' }}>
                  <div style={{ height:'3px', borderRadius:'2px', background: i <= zdStep ? D.or : D.b1, marginBottom:'4px', transition:'background 0.2s' }} />
                  <span style={{ fontSize:'9px', color: i <= zdStep ? D.or : D.tx3 }}>{label}</span>
                </div>
              ))}
            </div>

            {zdError && <div style={{ background:'rgba(239,91,91,0.1)', border:`1px solid rgba(239,91,91,0.3)`, borderRadius:'6px', padding:'8px 12px', marginBottom:'12px', fontSize:'11px', color:D.red }}>{zdError}</div>}

            {/* Étape 0 — Connexion */}
            {zdStep === 0 && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                  <div>
                    <div style={fieldLabel}>Sous-domaine Zendesk</div>
                    <input style={inp} placeholder="mon-entreprise" value={zdConfig.subdomain} onChange={e => setZdConfig({...zdConfig, subdomain:e.target.value})} />
                    <div style={{ fontSize:'9px', color:D.tx3, marginTop:'2px' }}>{zdConfig.subdomain || '...'}.zendesk.com</div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Email authentification</div>
                    <input style={inp} placeholder="agent@example.com" value={zdConfig.email_auth} onChange={e => setZdConfig({...zdConfig, email_auth:e.target.value})} />
                  </div>
                  <div>
                    <div style={fieldLabel}>API Token</div>
                    <input style={inp} type="password" placeholder="••••••••" value={zdConfig.api_token} onChange={e => setZdConfig({...zdConfig, api_token:e.target.value})} />
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <button
                    disabled={!zdConfig.subdomain || !zdConfig.email_auth || !zdConfig.api_token || zdLoading}
                    onClick={async () => {
                      setZdLoading(true); setZdError('');
                      try {
                        const r = await api.post(`/api/admin/workspaces/${selected.id}/connectors/zendesk/test`, { subdomain:zdConfig.subdomain, email_auth:zdConfig.email_auth, api_token:zdConfig.api_token });
                        if (r.data.success) {
                          setZdTestOk(true);
                          // Load groups, agents, forms
                          const [gr, ag, fo] = await Promise.all([
                            api.post(`/api/admin/workspaces/${selected.id}/connectors/zendesk/list-groups`, { subdomain:zdConfig.subdomain, email_auth:zdConfig.email_auth, api_token:zdConfig.api_token }).catch(()=>({data:{groups:[]}})),
                            api.post(`/api/admin/workspaces/${selected.id}/connectors/zendesk/list-agents`, { subdomain:zdConfig.subdomain, email_auth:zdConfig.email_auth, api_token:zdConfig.api_token }).catch(()=>({data:{agents:[]}})),
                            api.post(`/api/admin/workspaces/${selected.id}/connectors/zendesk/list-forms`, { subdomain:zdConfig.subdomain, email_auth:zdConfig.email_auth, api_token:zdConfig.api_token }).catch(()=>({data:{forms:[]}})),
                          ]);
                          setZdGroups(gr.data?.groups || []);
                          setZdAgents(ag.data?.agents || []);
                          setZdForms(fo.data?.forms || []);
                        } else { setZdError(r.data.error || 'Erreur de connexion'); setZdTestOk(false); }
                      } catch(e) { setZdError(e.response?.data?.detail || 'Erreur réseau'); setZdTestOk(false); }
                      finally { setZdLoading(false); }
                    }}
                    style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', opacity:!zdConfig.subdomain||!zdConfig.email_auth||!zdConfig.api_token||zdLoading?0.5:1, fontFamily:'DM Sans, sans-serif' }}
                  >
                    {zdLoading ? 'Test en cours...' : 'Tester la connexion'}
                  </button>
                  {zdTestOk && <span style={{ fontSize:'11px', color:D.green }}>Connexion OK</span>}
                </div>
                {zdTestOk && (
                  <div style={{ marginTop:'12px', textAlign:'right' }}>
                    <button onClick={() => setZdStep(1)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Suivant</button>
                  </div>
                )}
              </div>
            )}

            {/* Étape 1 — Filtrage */}
            {zdStep === 1 && (
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'12px' }}>Filtrez les tickets Zendesk entrants. Laissez vide pour tout accepter.</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                  <div>
                    <div style={fieldLabel}>Groupes acceptés</div>
                    <select style={inp} multiple value={zdConfig.zendesk_filter.group_ids||[]} onChange={e => {
                      const vals = Array.from(e.target.selectedOptions, o => o.value);
                      setZdConfig(c => ({...c, zendesk_filter:{...c.zendesk_filter, group_ids:vals}}));
                    }}>
                      {zdGroups.map(g => <option key={g.id} value={String(g.id)}>{g.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={fieldLabel}>Formulaires acceptés</div>
                    <select style={inp} multiple value={zdConfig.zendesk_filter.form_ids||[]} onChange={e => {
                      const vals = Array.from(e.target.selectedOptions, o => o.value);
                      setZdConfig(c => ({...c, zendesk_filter:{...c.zendesk_filter, form_ids:vals}}));
                    }}>
                      {zdForms.map(f => <option key={f.id} value={String(f.id)}>{f.name}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom:'12px' }}>
                  <div style={fieldLabel}>Tags requis (séparés par virgule)</div>
                  <input style={inp} placeholder="legal, formalite" value={(zdConfig.zendesk_filter.tags||[]).join(', ')} onChange={e => {
                    const tags = e.target.value.split(',').map(t => t.trim()).filter(Boolean);
                    setZdConfig(c => ({...c, zendesk_filter:{...c.zendesk_filter, tags}}));
                  }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <button onClick={() => setZdStep(0)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 16px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Retour</button>
                  <button onClick={() => setZdStep(2)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Suivant</button>
                </div>
              </div>
            )}

            {/* Étape 2 — Formalistes mapping */}
            {zdStep === 2 && (
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'12px' }}>Associez les agents Zendesk aux formalistes. Mode: un agent par formaliste ou pool partagé.</div>
                <div style={{ marginBottom:'12px' }}>
                  <div style={fieldLabel}>Mode d'assignation</div>
                  <select style={inp} value={zdConfig.agents_mode} onChange={e => setZdConfig(c => ({...c, agents_mode:e.target.value}))}>
                    <option value="individual">Individuel (1 agent = 1 formaliste)</option>
                    <option value="pool">Pool (tous les agents → même équipe)</option>
                  </select>
                </div>
                {zdAgents.length > 0 && zdConfig.agents_mode === 'individual' && (
                  <div style={{ marginBottom:'12px' }}>
                    <div style={fieldLabel}>Agents Zendesk détectés</div>
                    <div style={{ background:D.s2, borderRadius:'7px', padding:'8px', maxHeight:'160px', overflowY:'auto' }}>
                      {zdAgents.map(a => (
                        <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'4px 8px', fontSize:'11px', borderBottom:`1px solid ${D.b1}` }}>
                          <span style={{ color:D.tx }}>{a.name} ({a.email})</span>
                          <input style={{...inp, width:'140px', padding:'4px 8px', fontSize:'10px'}} placeholder="Formaliste Lexback" value={zdConfig.formaliste_mapping[a.id]||''} onChange={e => setZdConfig(c => ({...c, formaliste_mapping:{...c.formaliste_mapping, [a.id]:e.target.value}}))} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <button onClick={() => setZdStep(1)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 16px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Retour</button>
                  <button onClick={() => setZdStep(3)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Suivant</button>
                </div>
              </div>
            )}

            {/* Étape 3 — Signature & Activation */}
            {zdStep === 3 && (
              <div>
                <div style={{ fontSize:'11px', fontWeight:600, color:D.or, marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Signature White-Label</div>
                <div style={{ fontSize:'10px', color:D.tx3, marginBottom:'8px' }}>Variables : {'{{formaliste_prenom}}'}, {'{{formaliste_nom_initiale}}'}, {'{{signature_suffix}}'}</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                  <div>
                    <div style={fieldLabel}>Template signature</div>
                    <textarea style={{...inp, minHeight:'60px', resize:'vertical'}} value={zdConfig.signature_template} onChange={e => setZdConfig(c => ({...c, signature_template:e.target.value}))} />
                  </div>
                  <div>
                    <div style={fieldLabel}>Suffixe signature</div>
                    <input style={inp} placeholder="— Support Juridique" value={zdConfig.signature_suffix} onChange={e => setZdConfig(c => ({...c, signature_suffix:e.target.value}))} />
                    <div style={{ fontSize:'9px', color:D.red, marginTop:'4px' }}>Ne jamais inclure Lexback, Swap Business, Anthropic ou Claude</div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' }}>
                  <button
                    onClick={() => setZdConfig(c => ({...c, white_label_mode:!c.white_label_mode}))}
                    style={{ width:'36px', height:'20px', borderRadius:'10px', border:'none', cursor:'pointer', background:zdConfig.white_label_mode?D.green:D.s2, position:'relative', transition:'background 0.2s' }}
                  >
                    <div style={{ width:'16px', height:'16px', borderRadius:'50%', background:'#fff', position:'absolute', top:'2px', left:zdConfig.white_label_mode?'18px':'2px', transition:'left 0.2s' }} />
                  </button>
                  <span style={{ fontSize:'11px', color:D.tx2 }}>Mode white-label (vérification automatique)</span>
                </div>

                {/* Récapitulatif */}
                <div style={{ background:D.s2, borderRadius:'7px', padding:'12px', marginBottom:'12px' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', fontSize:'11px' }}>
                    <div><span style={{ color:D.tx3 }}>Sous-domaine:</span> <span style={{ color:D.tx }}>{zdConfig.subdomain}.zendesk.com</span></div>
                    <div><span style={{ color:D.tx3 }}>Mode agents:</span> <span style={{ color:D.tx }}>{zdConfig.agents_mode}</span></div>
                    <div><span style={{ color:D.tx3 }}>Filtres:</span> <span style={{ color:D.tx }}>{Object.values(zdConfig.zendesk_filter).flat().filter(Boolean).length || 'Aucun'}</span></div>
                    <div><span style={{ color:D.tx3 }}>White-label:</span> <span style={{ color:zdConfig.white_label_mode?D.green:D.red }}>{zdConfig.white_label_mode?'Actif':'Inactif'}</span></div>
                  </div>
                </div>

                {zdConnector?.webhook_url && (
                  <div style={{ marginBottom:'12px' }}>
                    <div style={fieldLabel}>URL Webhook (à configurer dans Zendesk → Webhooks)</div>
                    <div style={{ display:'flex', gap:'6px' }}>
                      <input style={{...inp, flex:1}} readOnly value={zdConnector.webhook_url} />
                      <button onClick={() => { navigator.clipboard.writeText(zdConnector.webhook_url); showToast('URL copiée'); }} style={{ background:D.s2, color:D.tx2, border:`1px solid ${D.b1}`, padding:'5px 10px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Copier</button>
                    </div>
                  </div>
                )}

                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <button onClick={() => setZdStep(2)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 16px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Retour</button>
                  <button
                    disabled={zdLoading}
                    onClick={async () => {
                      setZdLoading(true); setZdError('');
                      try {
                        const r = await api.post(`/api/admin/workspaces/${selected.id}/connectors/zendesk`, zdConfig);
                        setZdConnector(r.data);
                        setZdStep(-1);
                        showToast('Zendesk connecté et activé');
                      } catch(e) { setZdError(e.response?.data?.detail || "Erreur lors de l'activation"); }
                      finally { setZdLoading(false); }
                    }}
                    style={{ background:D.green, color:'#09090E', border:'none', padding:'7px 20px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', opacity:zdLoading?0.5:1, fontFamily:'DM Sans, sans-serif' }}
                  >
                    {zdLoading ? 'Activation...' : 'Activer Zendesk'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Intégrations Aircall ─── */}
      <div style={{ ...card, padding:'1rem', marginBottom:'1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: acStep >= 0 ? '16px' : 0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx }}>Aircall</div>
            {acConnector?.is_active ? (
              <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:D.green, background:'rgba(46,204,138,0.12)' }}>Connecté</span>
            ) : (
              <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color:D.tx3, background:D.s2 }}>Non configuré</span>
            )}
          </div>
          <div style={{ display:'flex', gap:'6px' }}>
            {acConnector?.is_active && (
              <>
                <button onClick={() => { setImportModal({ type:'aircall', connectorId:acConnector.id }); setImportFrom(''); setImportTo(''); }} style={{ background:'rgba(91,141,239,0.1)', color:D.blue, border:`1px solid rgba(91,141,239,0.3)`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                  Importer historique
                </button>
                <button onClick={async () => { try { await api.delete(`/api/admin/workspaces/${selected.id}/connectors/aircall`); setAcConnector(null); setAcStep(-1); showToast('Aircall déconnecté'); } catch { showToast('Erreur','error'); } }} style={{ background:'rgba(239,91,91,0.1)', color:D.red, border:`1px solid rgba(239,91,91,0.3)`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                  Déconnecter
                </button>
              </>
            )}
            <button onClick={() => { setAcStep(acStep >= 0 ? -1 : 0); setAcError(''); setAcTestOk(false); }} style={{ background:acStep >= 0 ? D.s2 : 'rgba(212,168,67,0.12)', color:acStep >= 0 ? D.tx2 : D.or, border:`1px solid ${acStep >= 0 ? D.b1 : 'rgba(212,168,67,0.3)'}`, padding:'5px 12px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
              {acStep >= 0 ? 'Fermer' : acConnector?.is_active ? 'Reconfigurer' : 'Configurer'}
            </button>
          </div>
        </div>

        {acStep >= 0 && (
          <div>
            {/* Stepper */}
            <div style={{ display:'flex', gap:'4px', marginBottom:'16px' }}>
              {['Connexion','Filtrage','Matching & Activation'].map((label, i) => (
                <div key={i} style={{ flex:1, textAlign:'center' }}>
                  <div style={{ height:'3px', borderRadius:'2px', background: i <= acStep ? D.or : D.b1, marginBottom:'4px', transition:'background 0.2s' }} />
                  <span style={{ fontSize:'9px', color: i <= acStep ? D.or : D.tx3 }}>{label}</span>
                </div>
              ))}
            </div>

            {acError && <div style={{ background:'rgba(239,91,91,0.1)', border:`1px solid rgba(239,91,91,0.3)`, borderRadius:'6px', padding:'8px 12px', marginBottom:'12px', fontSize:'11px', color:D.red }}>{acError}</div>}

            {/* Étape 0 — Connexion */}
            {acStep === 0 && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                  <div>
                    <div style={fieldLabel}>API ID Aircall</div>
                    <input style={inp} placeholder="abc123..." value={acConfig.api_id} onChange={e => setAcConfig({...acConfig, api_id:e.target.value})} />
                  </div>
                  <div>
                    <div style={fieldLabel}>API Token</div>
                    <input style={inp} type="password" placeholder="••••••••" value={acConfig.api_token} onChange={e => setAcConfig({...acConfig, api_token:e.target.value})} />
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <button
                    disabled={!acConfig.api_id || !acConfig.api_token || acLoading}
                    onClick={async () => {
                      setAcLoading(true); setAcError('');
                      try {
                        const r = await api.post(`/api/admin/workspaces/${selected.id}/connectors/aircall/test`, { api_id:acConfig.api_id, api_token:acConfig.api_token });
                        if (r.data.success) {
                          setAcTestOk(true);
                          const nr = await api.post(`/api/admin/workspaces/${selected.id}/connectors/aircall/list-numbers`, { api_id:acConfig.api_id, api_token:acConfig.api_token }).catch(()=>({data:{numbers:[]}}));
                          setAcNumbers(nr.data?.numbers || []);
                        } else { setAcError(r.data.error || 'Erreur de connexion'); setAcTestOk(false); }
                      } catch(e) { setAcError(e.response?.data?.detail || 'Erreur réseau'); setAcTestOk(false); }
                      finally { setAcLoading(false); }
                    }}
                    style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', opacity:!acConfig.api_id||!acConfig.api_token||acLoading?0.5:1, fontFamily:'DM Sans, sans-serif' }}
                  >
                    {acLoading ? 'Test en cours...' : 'Tester la connexion'}
                  </button>
                  {acTestOk && <span style={{ fontSize:'11px', color:D.green }}>Connexion OK</span>}
                </div>
                {acTestOk && (
                  <div style={{ marginTop:'12px', textAlign:'right' }}>
                    <button onClick={() => setAcStep(1)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Suivant</button>
                  </div>
                )}
              </div>
            )}

            {/* Étape 1 — Filtrage */}
            {acStep === 1 && (
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'12px' }}>Filtrez les appels entrants. Laissez vide pour tout accepter.</div>
                {acNumbers.length > 0 && (
                  <div style={{ marginBottom:'12px' }}>
                    <div style={fieldLabel}>Numéros Aircall acceptés</div>
                    <select style={inp} multiple value={acConfig.aircall_filter.number_ids||[]} onChange={e => {
                      const vals = Array.from(e.target.selectedOptions, o => o.value);
                      setAcConfig(c => ({...c, aircall_filter:{...c.aircall_filter, number_ids:vals}}));
                    }}>
                      {acNumbers.map(n => <option key={n.id} value={String(n.id)}>{n.name} ({n.digits})</option>)}
                    </select>
                  </div>
                )}
                <div style={{ marginBottom:'12px' }}>
                  <div style={fieldLabel}>Préfixes acceptés (séparés par virgule, ex: +33,+32)</div>
                  <input style={inp} placeholder="+33, +32" value={(acConfig.aircall_filter.accepted_digits||[]).join(', ')} onChange={e => {
                    const digits = e.target.value.split(',').map(t => t.trim()).filter(Boolean);
                    setAcConfig(c => ({...c, aircall_filter:{...c.aircall_filter, accepted_digits:digits}}));
                  }} />
                </div>
                <div style={{ marginBottom:'12px' }}>
                  <div style={fieldLabel}>Tags requis (séparés par virgule)</div>
                  <input style={inp} placeholder="legal, formalite" value={(acConfig.aircall_filter.tags||[]).join(', ')} onChange={e => {
                    const tags = e.target.value.split(',').map(t => t.trim()).filter(Boolean);
                    setAcConfig(c => ({...c, aircall_filter:{...c.aircall_filter, tags}}));
                  }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <button onClick={() => setAcStep(0)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 16px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Retour</button>
                  <button onClick={() => setAcStep(2)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Suivant</button>
                </div>
              </div>
            )}

            {/* Étape 2 — Matching & Activation */}
            {acStep === 2 && (
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'12px' }}>Options de matching et d'enrichissement automatique.</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                  <div>
                    <div style={fieldLabel}>Méthodes de matching</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                      {[{k:'phone',l:'Par numéro de téléphone'},{k:'email',l:'Par email contact'}].map(m => (
                        <label key={m.k} style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:D.tx2, cursor:'pointer' }}>
                          <input type="checkbox" checked={acConfig.match_by.includes(m.k)} onChange={e => {
                            setAcConfig(c => ({...c, match_by: e.target.checked ? [...c.match_by, m.k] : c.match_by.filter(x => x !== m.k)}));
                          }} style={{ accentColor:D.or }} />
                          {m.l}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={fieldLabel}>Options</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                        <button onClick={() => setAcConfig(c => ({...c, enrich_existing_dossiers:!c.enrich_existing_dossiers}))} style={{ width:'36px', height:'20px', borderRadius:'10px', border:'none', cursor:'pointer', background:acConfig.enrich_existing_dossiers?D.green:D.s2, position:'relative', transition:'background 0.2s' }}>
                          <div style={{ width:'16px', height:'16px', borderRadius:'50%', background:'#fff', position:'absolute', top:'2px', left:acConfig.enrich_existing_dossiers?'18px':'2px', transition:'left 0.2s' }} />
                        </button>
                        <span style={{ fontSize:'11px', color:D.tx2 }}>Enrichir les dossiers existants</span>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                        <button onClick={() => setAcConfig(c => ({...c, create_dossier_on_call:!c.create_dossier_on_call}))} style={{ width:'36px', height:'20px', borderRadius:'10px', border:'none', cursor:'pointer', background:acConfig.create_dossier_on_call?D.green:D.s2, position:'relative', transition:'background 0.2s' }}>
                          <div style={{ width:'16px', height:'16px', borderRadius:'50%', background:'#fff', position:'absolute', top:'2px', left:acConfig.create_dossier_on_call?'18px':'2px', transition:'left 0.2s' }} />
                        </button>
                        <span style={{ fontSize:'11px', color:D.tx2 }}>Créer dossier si non lié</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Récapitulatif */}
                <div style={{ background:D.s2, borderRadius:'7px', padding:'12px', marginBottom:'12px' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', fontSize:'11px' }}>
                    <div><span style={{ color:D.tx3 }}>API ID:</span> <span style={{ color:D.tx }}>{acConfig.api_id.slice(0,8)}...</span></div>
                    <div><span style={{ color:D.tx3 }}>Matching:</span> <span style={{ color:D.tx }}>{acConfig.match_by.join(', ')}</span></div>
                    <div><span style={{ color:D.tx3 }}>Filtres:</span> <span style={{ color:D.tx }}>{Object.values(acConfig.aircall_filter).flat().filter(Boolean).length || 'Aucun'}</span></div>
                    <div><span style={{ color:D.tx3 }}>Numéros:</span> <span style={{ color:D.tx }}>{(acConfig.aircall_filter.number_ids||[]).length || 'Tous'}</span></div>
                  </div>
                </div>

                {acConnector?.webhook_url && (
                  <div style={{ marginBottom:'12px' }}>
                    <div style={fieldLabel}>URL Webhook (à configurer dans Aircall → Webhooks)</div>
                    <div style={{ display:'flex', gap:'6px' }}>
                      <input style={{...inp, flex:1}} readOnly value={acConnector.webhook_url} />
                      <button onClick={() => { navigator.clipboard.writeText(acConnector.webhook_url); showToast('URL copiée'); }} style={{ background:D.s2, color:D.tx2, border:`1px solid ${D.b1}`, padding:'5px 10px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Copier</button>
                    </div>
                  </div>
                )}

                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <button onClick={() => setAcStep(1)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 16px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Retour</button>
                  <button
                    disabled={acLoading}
                    onClick={async () => {
                      setAcLoading(true); setAcError('');
                      try {
                        const r = await api.post(`/api/admin/workspaces/${selected.id}/connectors/aircall`, acConfig);
                        setAcConnector(r.data);
                        setAcStep(-1);
                        showToast('Aircall connecté et activé');
                      } catch(e) { setAcError(e.response?.data?.detail || "Erreur lors de l'activation"); }
                      finally { setAcLoading(false); }
                    }}
                    style={{ background:D.green, color:'#09090E', border:'none', padding:'7px 20px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', opacity:acLoading?0.5:1, fontFamily:'DM Sans, sans-serif' }}
                  >
                    {acLoading ? 'Activation...' : 'Activer Aircall'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Import History Modal ─── */}
      {importModal && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.7)' }} onClick={() => { if (!importProgress) { setImportModal(null); } }}>
          <div style={{ ...card, width:'440px', padding:'1.25rem' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'14px', fontWeight:600, color:D.tx, marginBottom:'16px' }}>
              Importer l'historique {importModal.type === 'airtable' ? 'Airtable' : importModal.type === 'zendesk' ? 'Zendesk' : 'Aircall'}
            </div>

            {importProgress ? (
              <div>
                <div style={{ marginBottom:'12px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:D.tx2, marginBottom:'6px' }}>
                    <span>{importProgress.status === 'running' ? 'Import en cours…' : importProgress.status === 'completed' ? 'Import terminé' : importProgress.status === 'failed' ? 'Erreur' : 'En attente…'}</span>
                    <span>{importProgress.total_fetched || 0} récupérés</span>
                  </div>
                  <div style={{ height:'6px', background:D.s2, borderRadius:'3px', overflow:'hidden' }}>
                    <div style={{ height:'100%', background: importProgress.status === 'completed' ? D.green : importProgress.status === 'failed' ? D.red : D.or, borderRadius:'3px', transition:'width 0.3s', width: importProgress.status === 'running' ? '60%' : importProgress.status === 'completed' ? '100%' : '100%' }} />
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'12px' }}>
                  <div style={{ background:D.s2, borderRadius:'6px', padding:'8px 10px', textAlign:'center' }}>
                    <div style={{ fontSize:'16px', fontWeight:600, color:D.tx }}>{importProgress.matched_count || 0}</div>
                    <div style={{ fontSize:'9px', color:D.tx3 }}>Matchés</div>
                  </div>
                  <div style={{ background:D.s2, borderRadius:'6px', padding:'8px 10px', textAlign:'center' }}>
                    <div style={{ fontSize:'16px', fontWeight:600, color:D.tx }}>{importProgress.orphans_count || 0}</div>
                    <div style={{ fontSize:'9px', color:D.tx3 }}>Orphelins</div>
                  </div>
                  <div style={{ background:D.s2, borderRadius:'6px', padding:'8px 10px', textAlign:'center' }}>
                    <div style={{ fontSize:'16px', fontWeight:600, color:D.tx }}>{importProgress.total_fetched || 0}</div>
                    <div style={{ fontSize:'9px', color:D.tx3 }}>Total</div>
                  </div>
                </div>
                {importProgress.error_message && (
                  <div style={{ background:'rgba(239,91,91,0.08)', border:'1px solid rgba(239,91,91,0.2)', borderRadius:'6px', padding:'8px 10px', fontSize:'10px', color:D.red, marginBottom:'12px' }}>
                    {importProgress.error_message}
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                  <button onClick={() => { setImportProgress(null); setImportModal(null); }} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                    Fermer
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
                  <div>
                    <div style={fieldLabel}>Date début</div>
                    <input style={inp} type="date" value={importFrom} onChange={e => setImportFrom(e.target.value)} />
                  </div>
                  <div>
                    <div style={fieldLabel}>Date fin</div>
                    <input style={inp} type="date" value={importTo} onChange={e => setImportTo(e.target.value)} />
                  </div>
                </div>
                <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'11px', color:D.tx2, marginBottom:'12px', cursor:'pointer' }}>
                  <input type="checkbox" checked={importDryRun} onChange={e => setImportDryRun(e.target.checked)} />
                  Mode simulation (compte sans créer)
                </label>
                <div style={{ fontSize:'10px', color:D.tx3, marginBottom:'12px' }}>Laissez vide pour importer les 90 derniers jours.</div>
                <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px' }}>
                  <button onClick={() => setImportModal(null)} style={{ background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 14px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>Annuler</button>
                  <button
                    disabled={importLoading}
                    onClick={async () => {
                      setImportLoading(true);
                      try {
                        const body = { dry_run: importDryRun };
                        if (importFrom) body.period_from = importFrom;
                        if (importTo) body.period_to = importTo;
                        const resp = await api.post(`/api/admin/workspaces/${selected.id}/connectors/${importModal.type}/import-history`, body);
                        const jobId = resp.data?.id;
                        showToast('Import lancé en arrière-plan');

                        // Poll progress every 3s
                        if (jobId) {
                          setImportProgress({ status: 'pending', total_fetched: 0, matched_count: 0, orphans_count: 0 });
                          const pollInterval = setInterval(async () => {
                            try {
                              const jr = await api.get(`/api/admin/import-jobs/${jobId}`);
                              const j = jr.data;
                              setImportProgress(j);
                              if (j.status === 'completed' || j.status === 'failed') {
                                clearInterval(pollInterval);
                                const r = await api.get(`/api/admin/workspaces/${selected.id}/import-jobs`);
                                setImportJobs(r.data || []);
                              }
                            } catch { clearInterval(pollInterval); }
                          }, 3000);
                        } else {
                          setImportModal(null);
                        }
                      } catch(e) { showToast(e.response?.data?.detail || 'Erreur import', 'error'); }
                      finally { setImportLoading(false); }
                    }}
                    style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', opacity:importLoading?0.5:1, fontFamily:'DM Sans, sans-serif' }}
                  >
                    {importLoading ? 'Lancement...' : importDryRun ? 'Lancer la simulation' : 'Lancer l\'import'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── Import Jobs History ─── */}
      {importJobs.length > 0 && (
        <div style={{ ...card, padding:'1rem', marginBottom:'1rem' }}>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx, marginBottom:'12px' }}>Historique des imports</div>
          <div style={{ fontSize:'11px' }}>
            {importJobs.slice(0,5).map(j => (
              <div key={j.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 0', borderBottom:`1px solid ${D.b1}` }}>
                <span style={{ color:D.tx2 }}>{j.source_type} · {j.started_at ? new Date(j.started_at).toLocaleString('fr-FR') : 'En attente'}</span>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{ color:D.tx3 }}>{j.total_fetched} récupérés · {j.matched_count} liés · {j.orphans_count} orphelins</span>
                  <Badge
                    label={j.status}
                    color={j.status==='completed'?D.green:j.status==='running'?D.blue:j.status==='failed'?D.red:D.tx3}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Créditer le wallet */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1rem' }}>
        <div style={{ ...card, padding:'1rem' }}>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx, marginBottom:'12px' }}>Créditer le wallet INPI</div>
          <div style={{ display:'flex', gap:'6px', marginBottom:'10px' }}>
            {[500,1000,3000,5000].map(m => (
              <button key={m} onClick={() => setCreditMontant(String(m))} style={{ flex:1, padding:'5px 0', background:creditMontant===String(m)?'rgba(212,168,67,0.15)':'transparent', border:`1px solid ${creditMontant===String(m)?D.or:D.b1}`, borderRadius:'5px', fontSize:'10px', color:creditMontant===String(m)?D.or:D.tx2, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                {m.toLocaleString('fr-FR')} €
              </button>
            ))}
          </div>
          <input style={{ ...inp, marginBottom:'10px' }} type="number" placeholder="Ou montant libre…" value={creditMontant} onChange={e => setCreditMontant(e.target.value)} />
          <button onClick={handleCredit} disabled={!creditMontant||saving} style={{ background:D.green, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'12px', fontWeight:500, cursor:'pointer', opacity:!creditMontant||saving?0.6:1, fontFamily:'DM Sans, sans-serif' }}>
            {saving?'En cours…':'Créditer'}
          </button>
        </div>

        {/* Infos + Actions */}
        <div style={{ ...card, padding:'1rem' }}>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx, marginBottom:'12px' }}>Informations</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:'4px', marginBottom:'12px' }}>
            {[
              ['Slug', selected.slug],
              ['SIREN', selected.siren||'—'],
              ['Mode INPI', selected.inpi_mode||'lexback'],
              ['Créé le', selected.created_at ? new Date(selected.created_at).toLocaleDateString('fr-FR') : '—'],
            ].map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:`1px solid ${D.b1}`, fontSize:'11px' }}>
                <span style={{ color:D.tx3 }}>{k}</span>
                <span style={{ color:D.tx, fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={handleActivate} style={{ background:'rgba(46,204,138,0.12)', color:D.green, border:`1px solid rgba(46,204,138,0.3)`, padding:'6px 14px', fontSize:'11px', borderRadius:'6px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
              Activer / R��activer
            </button>
            <button style={{ background:'rgba(239,91,91,0.1)', color:D.red, border:`1px solid rgba(239,91,91,0.3)`, padding:'6px 14px', fontSize:'11px', borderRadius:'6px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
              Suspendre
            </button>
          </div>
        </div>
      </div>

      {toast && <div style={{ position:'fixed', bottom:'1.5rem', right:'1.5rem', background:toast.type==='error'?D.red:D.green, color:'#fff', padding:'10px 16px', borderRadius:'8px', fontSize:'12px', zIndex:600 }}>{toast.msg}</div>}
    </div>
  );

  return (
    <div style={{ padding:'1.25rem 1.5rem', flex:1 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem' }}>
        <div>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:600, color:D.tx }}>Clients</div>
          <div style={{ fontSize:'11px', color:D.tx3, marginTop:'2px' }}>{workspaces.length} workspaces</div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 14px', borderRadius:'7px', fontSize:'12px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
          + Nouveau client
        </button>
      </div>

      {showCreate && (
        <div style={{ ...card, padding:'1.1rem', marginBottom:'1rem', border:`1px solid ${D.or}30` }}>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx, marginBottom:'12px' }}>Créer un nouveau client</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'8px', marginBottom:'10px' }}>
            {[
              { l:'Nom workspace', k:'name', ph:'Ex: LegalVision' },
              { l:'Email admin',   k:'email', ph:'admin@client.com' },
              { l:'SIREN',         k:'siren', ph:'123456789' },
            ].map(f => (
              <div key={f.k}>
                <div style={{ fontSize:'9px', textTransform:'uppercase', letterSpacing:'0.07em', color:D.tx3, marginBottom:'4px' }}>{f.l}</div>
                <input style={inp} placeholder={f.ph} value={newClient[f.k]} onChange={e => setNewClient(p=>({...p,[f.k]:e.target.value}))} />
              </div>
            ))}
            <div>
              <div style={{ fontSize:'9px', textTransform:'uppercase', letterSpacing:'0.07em', color:D.tx3, marginBottom:'4px' }}>Plan</div>
              <select style={inp} value={newClient.plan} onChange={e => setNewClient(p=>({...p,plan:e.target.value}))}>
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={handleCreateClient} disabled={creatingClient} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'12px', fontWeight:500, cursor:creatingClient?'default':'pointer', opacity:creatingClient?0.6:1, fontFamily:'DM Sans, sans-serif' }}>
              {creatingClient ? 'Création…' : 'Créer le client'}
            </button>
            <button onClick={() => setShowCreate(false)} style={{ background:'transparent', border:`1px solid ${D.b1}`, color:D.tx2, padding:'7px 14px', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <div style={{ ...card, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${D.b1}` }}>
              {['Client','Plan','Wallet','Forfait','Statut','Actions'].map(h => (
                <th key={h} style={{ textAlign:'left', padding:'10px 14px', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:D.tx3, fontWeight:400 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {workspaces.map((w,i) => (
              <tr key={w.id} style={{ borderBottom:i<workspaces.length-1?`1px solid ${D.b1}`:'none' }}
                onMouseEnter={e => e.currentTarget.style.background=D.s2}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}
              >
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ fontSize:'12px', fontWeight:500, color:D.tx }}>{w.name}</div>
                  <div style={{ fontSize:'10px', color:D.tx3 }}>{w.slug}</div>
                </td>
                <td style={{ padding:'10px 14px' }}><Badge label={w.plan} color={PLAN_COLOR[w.plan]||D.tx3} /></td>
                <td style={{ padding:'10px 14px', fontSize:'12px', fontWeight:500, color:Number(w.wallet_balance||0)<3000?D.red:D.green }}>
                  {Number(w.wallet_balance||0).toLocaleString('fr-FR')} €
                </td>
                <td style={{ padding:'10px 14px', fontSize:'12px', color:D.tx2 }}>{Number(w.forfait_mensuel||0).toLocaleString('fr-FR')} €</td>
                <td style={{ padding:'10px 14px' }}><Badge label={w.status==='active'?'Actif':'Inactif'} color={w.status==='active'?D.green:D.amber} /></td>
                <td style={{ padding:'10px 14px' }}>
                  <button onClick={() => setSelected(w)} style={{ background:'transparent', border:`1px solid ${D.b1}`, color:D.tx2, padding:'4px 10px', fontSize:'10px', borderRadius:'5px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                    Gérer →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── EQUIPES ─────────────────────────────────────────────────────────────────
// ─── EQUIPES (branchée sur /api/admin/users) ─────────────────────────────────
function Equipes() {
  const { user: currentAdmin, startImpersonation } = useAuth();
  const [users, setUsers]             = useState([]);
  const [workspaces, setWorkspaces]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState('');
  const [filterRole, setFilterRole]   = useState('');
  const [filterWs, setFilterWs]       = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showCreate, setShowCreate]   = useState(false);
  const [toast, setToast]             = useState(null);
  const [saving, setSaving]           = useState(false);
  const [newUser, setNewUser]         = useState({ prenom:'', nom:'', email:'', password:'', role:'formaliste', workspace_id:'' });
  const [editingUser, setEditingUser] = useState(null); // user en cours d'édition
  const [editData, setEditData]       = useState({ prenom:'', nom:'', email:'', role:'', workspace_id:'', newPassword:'' });

  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null), 3000); };

  // Chargement workspaces pour le filtre + form création
  useEffect(() => {
    api.get('/api/admin/workspaces').then(r => setWorkspaces(r.data||[])).catch(()=>{});
  }, []);

  // Chargement users (avec refresh à chaque changement de filtre)
  const loadUsers = () => {
    setLoading(true);
    setError(null);
    const params = {};
    if (search.trim())   params.search = search.trim();
    if (filterRole)      params.role = filterRole;
    if (filterWs)        params.workspace_id = filterWs;
    if (showInactive)    params.show_inactive = true;
    api.get('/api/admin/users', { params })
      .then(r => { setUsers(r.data||[]); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || 'Erreur de chargement'); setLoading(false); });
  };

  // Debounce sur la recherche
  useEffect(() => {
    const t = setTimeout(loadUsers, search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterRole, filterWs, showInactive]);

  // Création d'un user
  const handleCreate = async () => {
    if (!newUser.prenom || !newUser.nom || !newUser.email || !newUser.password) {
      showToast('Tous les champs requis', 'error'); return;
    }
    if (newUser.password.length < 8) {
      showToast('Mot de passe : 8 caractères min.', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = { ...newUser };
      if (!payload.workspace_id) delete payload.workspace_id;
      await api.post('/api/admin/users', payload);
      showToast('Utilisateur créé ✓');
      setShowCreate(false);
      setNewUser({ prenom:'', nom:'', email:'', password:'', role:'formaliste', workspace_id:'' });
      loadUsers();
    } catch(err) {
      showToast(err.response?.data?.detail || 'Erreur', 'error');
    } finally { setSaving(false); }
  };

  // Activation / désactivation
  const handleToggle = async (user) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    const msg    = user.is_active
      ? `Désactiver ${user.prenom} ${user.nom} ?\nIl ne pourra plus se connecter. Ses données (dossiers, historique) sont conservées.`
      : `Réactiver ${user.prenom} ${user.nom} ?`;
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/api/admin/users/${user.id}/${action}`);
      showToast(user.is_active ? 'Désactivé ✓' : 'Réactivé ✓');
      loadUsers();
    } catch(err) {
      showToast(err.response?.data?.detail || 'Erreur', 'error');
    }
  };

  // Ouvrir la modal d'édition
  const openEdit = (user) => {
    setEditingUser(user);
    setEditData({
      prenom: user.prenom || '',
      nom: user.nom || '',
      email: user.email || '',
      role: user.role || 'formaliste',
      workspace_id: user.workspace_id || '',
      newPassword: '',
    });
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      // Construire le payload uniquement avec les champs modifiés
      const payload = {};
      if (editData.prenom !== editingUser.prenom) payload.prenom = editData.prenom;
      if (editData.nom !== editingUser.nom) payload.nom = editData.nom;
      if (editData.email !== editingUser.email) payload.email = editData.email;
      if (editData.role !== editingUser.role) payload.role = editData.role;
      if (editData.workspace_id !== (editingUser.workspace_id || '')) {
        payload.workspace_id = editData.workspace_id;
      }

      // Appliquer les modifs d'infos si il y en a
      if (Object.keys(payload).length > 0) {
        await api.patch(`/api/admin/users/${editingUser.id}`, payload);
      }

      // Réinitialiser le mot de passe si un nouveau est fourni
      if (editData.newPassword && editData.newPassword.length > 0) {
        if (editData.newPassword.length < 8) {
          showToast('Mot de passe : 8 caractères min.', 'error');
          setSaving(false);
          return;
        }
        await api.post(`/api/admin/users/${editingUser.id}/reset-password`, {
          new_password: editData.newPassword,
        });
      }

      showToast('Modifications enregistrées ✓');
      setEditingUser(null);
      loadUsers();
    } catch(err) {
      showToast(err.response?.data?.detail || 'Erreur', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Impersonation
  const handleImpersonate = async () => {
    if (!editingUser) return;
    try {
      const res = await api.post(`/api/admin/impersonate/${editingUser.id}`);
      const { access_token, user: targetUser } = res.data;
      startImpersonation(access_token, targetUser, currentAdmin);
      window.location.href = '/dashboard';
    } catch(err) {
      showToast(err.response?.data?.detail || 'Erreur impersonation', 'error');
    }
  };

  // KPIs calculés depuis les données réelles
  const nTotal     = users.length;
  const nActifs    = users.filter(u => u.is_active).length;
  const nInactifs  = users.filter(u => !u.is_active).length;
  const nFormalistes = users.filter(u => u.role === 'formaliste').length;
  const totalDossiers = users.reduce((s,u) => s + (u.dossiers_count||0), 0);

  // Styles partagés
  const inp = { width:'100%', padding:'7px 11px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'7px', fontSize:'12px', color:D.tx, outline:'none', fontFamily:'DM Sans, sans-serif' };

  const ROLE_COLOR = { admin:'#9B7FE8', superviseur:D.blue, formaliste:D.green, client:D.or, support:D.amber };

  const initials = (u) => ((u.prenom?.[0]||'?') + (u.nom?.[0]||'?')).toUpperCase();
  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'2-digit' })
    : '—';

  return (
    <div style={{ padding:'1.25rem 1.5rem', flex:1, position:'relative' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem' }}>
        <div>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:600, color:D.tx }}>Équipes</div>
          <div style={{ fontSize:'11px', color:D.tx3, marginTop:'2px' }}>
            {loading ? 'Chargement…' : `${nTotal} utilisateur${nTotal>1?'s':''} · ${nFormalistes} formaliste${nFormalistes>1?'s':''}`}
          </div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 14px', borderRadius:'7px', fontSize:'12px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
          + Ajouter un utilisateur
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'1.25rem' }}>
        <KpiCard label="Total équipe"        value={nTotal}          delta="Tous rôles confondus" />
        <KpiCard label="Actifs"              value={nActifs}         color={D.green} delta={`${nInactifs} désactivé${nInactifs>1?'s':''}`} />
        <KpiCard label="Formalistes"         value={nFormalistes}    delta="Opérationnels" />
        <KpiCard label="Dossiers assignés"   value={totalDossiers}   color={D.or} delta="Charge totale équipe" />
      </div>

      {/* Filtres */}
      <div style={{ ...card, padding:'10px 12px', marginBottom:'10px', display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
        <input
          style={{ ...inp, flex:1, minWidth:'220px' }}
          type="text"
          placeholder="Rechercher par nom, prénom, email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ ...inp, width:'150px' }}>
          <option value="">Tous les rôles</option>
          <option value="admin">Admin</option>
          <option value="superviseur">Superviseur</option>
          <option value="formaliste">Formaliste</option>
          <option value="client">Client</option>
          <option value="support">Support</option>
        </select>
        <select value={filterWs} onChange={e => setFilterWs(e.target.value)} style={{ ...inp, width:'180px' }}>
          <option value="">Tous les workspaces</option>
          {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:D.tx2, cursor:'pointer', whiteSpace:'nowrap' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Afficher désactivés
        </label>
      </div>

      {/* Tableau users */}
      <div style={{ ...card, overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding:'3rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>Chargement…</div>
        ) : error ? (
          <div style={{ padding:'3rem', textAlign:'center', color:D.red, fontSize:'12px' }}>⚠ {error}</div>
        ) : users.length === 0 ? (
          <div style={{ padding:'3rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>
            Aucun utilisateur ne correspond à ces filtres.
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${D.b1}` }}>
                {['Utilisateur','Email','Rôle','Workspace','Dossiers','Dernière connexion','Statut','Action'].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'10px 14px', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:D.tx3, fontWeight:400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const roleColor = ROLE_COLOR[u.role] || D.tx3;
                return (
                  <tr key={u.id} style={{
                    borderBottom: i<users.length-1 ? `1px solid ${D.b1}` : 'none',
                    opacity: u.is_active ? 1 : 0.45,
                  }}
                    onMouseEnter={e => e.currentTarget.style.background=D.s2}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <div style={{ width:'28px', height:'28px', borderRadius:'50%', background:`${roleColor}20`, border:`1px solid ${roleColor}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'9px', fontWeight:700, color:roleColor, flexShrink:0 }}>
                          {initials(u)}
                        </div>
                        <div>
                          <div style={{ fontSize:'12px', fontWeight:500, color:D.tx }}>{u.prenom} {u.nom}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:'11px', color:D.tx2 }}>{u.email}</td>
                    <td style={{ padding:'10px 14px' }}>
                      <Badge label={u.role} color={roleColor} />
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:'11px', color:D.tx2 }}>
                      {u.workspace_name || <span style={{ color:D.tx3 }}>—</span>}
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:'11px', color:u.dossiers_count>0?D.tx:D.tx3 }}>
                      {u.dossiers_count || 0}
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:'11px', color:D.tx3 }}>{fmtDate(u.last_login)}</td>
                    <td style={{ padding:'10px 14px' }}>
                      {u.is_active
                        ? <Badge label="Actif" color={D.green} />
                        : <Badge label="Désactivé" color={D.red} />
                      }
                    </td>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', gap:'6px' }}>
                        <button onClick={() => openEdit(u)} style={{
                          background:'transparent', color:D.tx2,
                          border:`1px solid ${D.b1}`,
                          padding:'4px 10px', fontSize:'10px', borderRadius:'5px', cursor:'pointer',
                          fontFamily:'DM Sans, sans-serif', whiteSpace:'nowrap',
                        }}>
                          Modifier
                        </button>
                        <button onClick={() => handleToggle(u)} style={{
                          background: u.is_active ? 'transparent' : `${D.or}18`,
                          color: u.is_active ? D.tx2 : D.or,
                          border: `1px solid ${u.is_active ? D.b1 : D.or}40`,
                          padding:'4px 10px', fontSize:'10px', borderRadius:'5px', cursor:'pointer',
                          fontFamily:'DM Sans, sans-serif', whiteSpace:'nowrap',
                        }}>
                          {u.is_active ? 'Désactiver' : 'Réactiver'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal création user */}
      {showCreate && (
        <div onClick={() => setShowCreate(false)} style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.65)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:500, padding:'1rem',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            ...card, padding:'1.5rem', width:'100%', maxWidth:'480px',
          }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'15px', fontWeight:600, color:D.tx, marginBottom:'4px' }}>
              Nouvel utilisateur
            </div>
            <div style={{ fontSize:'11px', color:D.tx3, marginBottom:'1.2rem' }}>
              Crée un compte pour un formaliste, superviseur ou autre membre de l'équipe.
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Prénom</div>
                <input style={inp} type="text" value={newUser.prenom} onChange={e => setNewUser({...newUser, prenom:e.target.value})} />
              </div>
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Nom</div>
                <input style={inp} type="text" value={newUser.nom} onChange={e => setNewUser({...newUser, nom:e.target.value})} />
              </div>
            </div>
            <div style={{ marginBottom:'10px' }}>
              <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Email</div>
              <input style={inp} type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email:e.target.value})} />
            </div>
            <div style={{ marginBottom:'10px' }}>
              <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Mot de passe (min 8 car.)</div>
              <input style={inp} type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password:e.target.value})} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'1.2rem' }}>
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Rôle</div>
                <select style={inp} value={newUser.role} onChange={e => setNewUser({...newUser, role:e.target.value})}>
                  <option value="formaliste">Formaliste</option>
                  <option value="superviseur">Superviseur</option>
                  <option value="admin">Admin</option>
                  <option value="client">Client</option>
                  <option value="support">Support</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Workspace (optionnel)</div>
                <select style={inp} value={newUser.workspace_id} onChange={e => setNewUser({...newUser, workspace_id:e.target.value})}>
                  <option value="">— Aucun —</option>
                  {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
              <button onClick={() => setShowCreate(false)} style={{ background:'transparent', border:`1px solid ${D.b1}`, color:D.tx2, padding:'7px 14px', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                Annuler
              </button>
              <button onClick={handleCreate} disabled={saving} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 14px', borderRadius:'6px', fontSize:'12px', fontWeight:500, cursor:'pointer', opacity:saving?0.6:1, fontFamily:'DM Sans, sans-serif' }}>
                {saving ? 'Création…' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal édition user */}
      {editingUser && (
        <div onClick={() => !saving && setEditingUser(null)} style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.65)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:500, padding:'1rem',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            ...card, padding:'1.5rem', width:'100%', maxWidth:'520px', maxHeight:'90vh', overflowY:'auto',
          }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'15px', fontWeight:600, color:D.tx, marginBottom:'4px' }}>
              Modifier l'utilisateur
            </div>
            <div style={{ fontSize:'11px', color:D.tx3, marginBottom:'1.2rem' }}>
              {editingUser.prenom} {editingUser.nom} · {editingUser.email}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Prénom</div>
                <input style={inp} type="text" value={editData.prenom} onChange={e => setEditData({...editData, prenom:e.target.value})} />
              </div>
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Nom</div>
                <input style={inp} type="text" value={editData.nom} onChange={e => setEditData({...editData, nom:e.target.value})} />
              </div>
            </div>
            <div style={{ marginBottom:'10px' }}>
              <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Email</div>
              <input style={inp} type="email" value={editData.email} onChange={e => setEditData({...editData, email:e.target.value})} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Rôle</div>
                <select style={inp} value={editData.role} onChange={e => setEditData({...editData, role:e.target.value})}>
                  <option value="formaliste">Formaliste</option>
                  <option value="superviseur">Superviseur</option>
                  <option value="admin">Admin</option>
                  <option value="client">Client</option>
                  <option value="support">Support</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize:'11px', color:D.tx2, marginBottom:'4px' }}>Workspace</div>
                <select style={inp} value={editData.workspace_id} onChange={e => setEditData({...editData, workspace_id:e.target.value})}>
                  <option value="">— Aucun —</option>
                  {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
            </div>

            {/* Séparateur */}
            <div style={{ borderTop:`1px solid ${D.b1}`, margin:'1.2rem 0 1rem', paddingTop:'1rem' }}>
              <div style={{ fontSize:'11px', fontWeight:600, color:D.tx2, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                Réinitialiser le mot de passe
              </div>
              <div style={{ fontSize:'11px', color:D.tx3, marginBottom:'8px' }}>
                Laisse vide si tu ne veux pas changer le mot de passe. Transmets le nouveau mot de passe au user via un canal sécurisé.
              </div>
              <input
                style={inp}
                type="text"
                placeholder="Nouveau mot de passe (min 8 caractères)"
                value={editData.newPassword}
                onChange={e => setEditData({...editData, newPassword:e.target.value})}
              />
            </div>

            {/* Impersonation — uniquement pour les non-admin */}
            {editingUser.role !== 'admin' && editingUser.is_active && (
              <div style={{ borderTop:`1px solid ${D.b1}`, margin:'1.2rem 0 0', paddingTop:'1rem' }}>
                <div style={{ fontSize:'11px', fontWeight:600, color:D.tx2, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Connexion en tant que
                </div>
                <div style={{ fontSize:'11px', color:D.tx3, marginBottom:'8px' }}>
                  Se connecter sur l'app avec l'identité de cet utilisateur. Session limitée à 1h.
                </div>
                <button
                  onClick={handleImpersonate}
                  style={{
                    background:'rgba(240,168,67,0.12)', color:D.amber,
                    border:`1px solid rgba(240,168,67,0.3)`,
                    padding:'7px 14px', borderRadius:'6px', fontSize:'12px',
                    fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif',
                  }}
                >
                  Se connecter en tant que {editingUser.prenom} {editingUser.nom}
                </button>
              </div>
            )}

            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'1.2rem' }}>
              <button onClick={() => setEditingUser(null)} disabled={saving} style={{ background:'transparent', border:`1px solid ${D.b1}`, color:D.tx2, padding:'7px 14px', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontFamily:'DM Sans, sans-serif', opacity:saving?0.6:1 }}>
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving} style={{ background:D.or, color:'#09090E', border:'none', padding:'7px 14px', borderRadius:'6px', fontSize:'12px', fontWeight:500, cursor:'pointer', opacity:saving?0.6:1, fontFamily:'DM Sans, sans-serif' }}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position:'fixed', bottom:'1.5rem', right:'1.5rem',
          background: toast.type==='error' ? D.red : D.green,
          color:'#fff', padding:'10px 16px', borderRadius:'7px',
          fontSize:'12px', fontWeight:500, zIndex:600,
          boxShadow:'0 6px 20px rgba(0,0,0,0.4)',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── OPERATIONS ──────────────────────────────────────────────────────────────

const OP_CAPACITE = 12;
const OP_STATUS_COLORS = {
  recu:'#6366f1', assigne:'#8b5cf6', en_cours:D.or, depose:D.blue,
  termine:D.green, rejete:D.red, doc_manquant:D.amber, bloque:D.red,
  en_attente:D.amber,
};
const OP_STATUS_LABELS = {
  recu:'Recu', assigne:'Assigne', en_cours:'En cours', depose:'Depose',
  termine:'Termine', rejete:'Rejete', doc_manquant:'Doc manquant',
  bloque:'Bloque', en_attente:'En attente',
};

function opGetInitials(nom) {
  if (!nom) return '?';
  return nom.trim().split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}
function opFormatDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' }) +
    ' ' + dt.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });
}

function OpStatusBadge({ status }) {
  const color = OP_STATUS_COLORS[status] || '#888';
  return (
    <span style={{ fontSize:'10px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color, background:`${color}18` }}>
      {OP_STATUS_LABELS[status] || status}
    </span>
  );
}

function OpFrmAvatar({ nom, count }) {
  const pct = count != null ? count / OP_CAPACITE : 0;
  const capColor = pct >= 1 ? D.red : pct >= 0.75 ? D.amber : D.green;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
      <div style={{ position:'relative' }}>
        <div style={{
          width:'28px', height:'28px', borderRadius:'50%',
          background:`linear-gradient(135deg, ${D.or}33, ${D.or}11)`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'10px', fontWeight:600, color:D.or,
          border:`1.5px solid ${D.or}44`,
        }}>
          {opGetInitials(nom)}
        </div>
        {count != null && (
          <div style={{
            position:'absolute', bottom:'-1px', right:'-1px',
            width:'9px', height:'9px', borderRadius:'50%',
            background:capColor, border:`2px solid ${D.s1}`,
          }} />
        )}
      </div>
      <span style={{ fontSize:'12px', color:D.tx }}>{nom}</span>
    </div>
  );
}

// ─── Admin Dossier Detail Modal (dark theme) ────────────────────────────────
// Mapping subcategory/airtable_column_name → catégorie métier admin
// Ordre = ordre d'affichage dans le modal
const ADMIN_DOC_CATEGORIES = [
  'Dossier non signé',
  'Statuts',
  'PV',
  'Kbis',
  'RBE',
  'Synthèse',
  'Dossier signé',
  'Acte de cession',
  'Autres documents',
];

function adminClassifyDoc(att) {
  const raw = (att.subcategory || att.airtable_column_name || att.type_document || '').toLowerCase();
  if (raw.includes('non signé') || raw.includes('non signe') || raw.includes('dossier non')) return 'Dossier non signé';
  if (raw.includes('dossier signé') || raw.includes('dossier signe') || (raw.includes('dossier') && raw.includes('signé'))) return 'Dossier signé';
  if (raw.includes('cession')) return 'Acte de cession';
  if (raw.includes('statut')) return 'Statuts';
  if (raw.includes('pv') || raw.includes('procès-verbal') || raw.includes('proces-verbal') || raw.includes('age') || raw.includes('ago')) return 'PV';
  if (raw.includes('kbis') || raw.includes('extrait')) return 'Kbis';
  if (raw.includes('rbe') || raw.includes('bénéf') || raw.includes('benef')) return 'RBE';
  if (raw.includes('synthèse') || raw.includes('synthese') || raw.includes('inpi')) return 'Synthèse';
  return 'Autres documents';
}

// ─── Composant Discussion pour le modal admin (dark theme) ──
// Affiche les notes d'un dossier filtrées par visibility (shared ou internal)
// et permet d'en poster une nouvelle.
function AdminDiscussion({ dossierId, visibility, title, icon }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/notes/${dossierId}`);
      const arr = Array.isArray(res.data) ? res.data : [];
      setMessages(arr.filter(m => m.visibility === visibility));
    } catch { setMessages([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dossierId, visibility]);

  const send = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/api/notes/${dossierId}`, { content: draft.trim(), visibility });
      setDraft('');
      await load();
    } catch (e) {
      console.error('Erreur envoi note', e);
    } finally { setSending(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const dt = new Date(iso);
    const diff = (Date.now() - dt.getTime()) / 1000;
    if (diff < 60) return 'à l\'instant';
    if (diff < 3600) return `il y a ${Math.floor(diff/60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff/3600)} h`;
    if (diff < 7*86400) return `il y a ${Math.floor(diff/86400)} j`;
    return dt.toLocaleDateString('fr-FR', { day:'2-digit', month:'short' });
  };

  const accentColor = visibility === 'internal' ? D.amber : D.green;

  return (
    <div style={{ marginTop:'14px', padding:'12px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'8px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'10px' }}>
        <span style={{ fontSize:'13px' }}>{icon}</span>
        <span style={{ fontSize:'12px', fontWeight:600, color:D.tx, letterSpacing:'0.01em' }}>{title}</span>
        {messages.length > 0 && (
          <span style={{ fontSize:'10px', fontWeight:500, color:accentColor, background:`${accentColor}15`, padding:'1px 7px', borderRadius:'10px' }}>{messages.length}</span>
        )}
      </div>

      {loading ? (
        <div style={{ fontSize:'11px', color:D.tx3 }}>Chargement…</div>
      ) : messages.length === 0 ? (
        <div style={{ fontSize:'11px', color:D.tx3, fontStyle:'italic' }}>Aucun message pour l'instant.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px', maxHeight:'220px', overflowY:'auto', marginBottom:'10px' }}>
          {messages.map(m => (
            <div key={m.id} style={{ padding:'8px 10px', background:D.s1, borderRadius:'6px', border:`0.5px solid ${D.b1}`, borderLeft:`2px solid ${accentColor}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'3px' }}>
                <span style={{ fontSize:'10.5px', fontWeight:600, color:D.tx }}>
                  {m.author_first_name ? `${m.author_first_name}${m.author_last_name ? ' ' + m.author_last_name : ''}` : (m.author_email || 'Utilisateur')}
                </span>
                <span style={{ fontSize:'9.5px', color:D.tx3 }}>{fmtDate(m.created_at)}</span>
              </div>
              <div style={{ fontSize:'11.5px', color:D.tx2, whiteSpace:'pre-wrap', lineHeight:'1.5' }}>{m.content}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'flex', gap:'6px' }}>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
          placeholder={visibility === 'internal' ? 'Note interne Lexback (invisible client)…' : 'Message visible par le client…'}
          rows={2}
          style={{
            flex:1,
            padding:'7px 10px',
            background:D.s1,
            border:`0.5px solid ${D.b1}`,
            borderRadius:'6px',
            color:D.tx,
            fontSize:'11.5px',
            fontFamily:'inherit',
            resize:'vertical',
            outline:'none',
          }}
        />
        <button
          onClick={send}
          disabled={!draft.trim() || sending}
          style={{
            padding:'7px 14px',
            background: draft.trim() && !sending ? accentColor : D.s1,
            color: draft.trim() && !sending ? '#1a1a1a' : D.tx3,
            border:`0.5px solid ${draft.trim() && !sending ? accentColor : D.b1}`,
            borderRadius:'6px',
            fontSize:'11px',
            fontWeight:600,
            cursor: draft.trim() && !sending ? 'pointer' : 'not-allowed',
            fontFamily:'inherit',
            alignSelf:'flex-end',
          }}
        >
          {sending ? '…' : 'Envoyer'}
        </button>
      </div>
    </div>
  );
}

// ─── EditableCardImpl : composant externe stable (sinon perte de focus) ────
function EditableCardImpl({ label, value, isDirty, type = 'text', options, placeholder = '—', onChange }) {
  const baseStyle = {
    background:'transparent', border:'none', color:D.tx,
    fontSize:'12px', fontFamily:'inherit', fontWeight:500,
    outline:'none', width:'100%', padding:'1px 0',
  };
  return (
    <div style={{
      background: D.s2, borderRadius:'6px',
      padding:'8px 10px',
      border:`1px solid ${isDirty ? D.or : D.b1}`,
      transition:'border-color 0.15s',
    }}>
      <div style={{ fontSize:'9px', textTransform:'uppercase', color:isDirty ? D.or : D.tx3, letterSpacing:'0.04em', marginBottom:'2px', display:'flex', justifyContent:'space-between' }}>
        <span>{label}</span>
        {isDirty && <span style={{ fontSize:'9px' }}>● modifié</span>}
      </div>
      {type === 'select' ? (
        <select
          value={value}
          onChange={e => onChange(e.target.value || null)}
          style={{ ...baseStyle, cursor:'pointer' }}
        >
          <option value="">{placeholder}</option>
          {options?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={{ ...baseStyle, resize:'vertical', minHeight:'48px', lineHeight:1.4 }}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={baseStyle}
        />
      )}
    </div>
  );
}

function AdminDossierDetailModal({ dossier, onClose, formalistesMap }) {
  const [docs, setDocs] = useState([]);
  const [attachments, setAttachments] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadCat, setUploadCat] = useState('Statuts');
  const [uploadFiles, setUploadFiles] = useState([]);  // tableau pour multi-upload
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  // ─── Mode édition global (TOUT éditable dans la modale) ────────────────
  // edits contient les valeurs modifiées localement avant Save.
  // Initialisé depuis dossier au mount, fusionné avec dossier pour l'affichage.
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);

  // Vue fusionnée (priorité aux edits)
  const cur = { ...dossier, ...edits };
  const dirty = Object.keys(edits).length > 0;

  // Helper : update un champ local
  const setField = (key, value) => {
    setEdits(prev => {
      // Si la nouvelle valeur === valeur initiale, on retire l'entrée pour clean dirty
      if (value === (dossier[key] ?? '') || (value === '' && (dossier[key] == null))) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
    setSaveOk(false);
  };

  // Catalogue : formalistes pickables, statuts, types — chargé une fois
  const [formalistesOptions, setFormalistesOptions] = useState([]);
  useEffect(() => {
    if (!dossier?.workspace_id) return;
    api.get(`/api/admin/formalistes-pickable?workspace_id=${dossier.workspace_id}`)
      .then(r => {
        const fmts = [
          ...(r.data?.lexback || []).map(f => ({ ...f, _group: 'Lexback' })),
          ...(r.data?.client || []).map(f => ({ ...f, _group: 'Client' })),
        ];
        setFormalistesOptions(fmts);
      })
      .catch(() => {});
  }, [dossier?.workspace_id]);

  // Sauvegarde globale
  const handleSaveAll = async () => {
    if (!dirty) return;
    setSaving(true);
    setSaveError('');
    setSaveOk(false);
    try {
      await api.patch(`/api/admin/dossiers/${dossier.id}`, edits);
      setSaveOk(true);
      setEdits({}); // clean dirty
      // Refresh local dossier (idéalement reload parent — pour l'instant on met juste OK)
      setTimeout(() => setSaveOk(false), 3000);
    } catch (e) {
      setSaveError(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Suppression d'un document attachment
  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm('Supprimer ce document définitivement ?')) return;
    try {
      await api.delete(`/api/documents/attachment/${attId}`);
      await loadAttachments();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const loadAttachments = useCallback(async () => {
    if (!dossier) return;
    try {
      const a = await api.get(`/api/documents/attachments/${dossier.id}`).then(r => r.data).catch(() => ({}));
      setAttachments(a || {});
    } catch {}
  }, [dossier]);

  useEffect(() => {
    if (!dossier) return;
    setLoading(true);
    Promise.all([
      api.get(`/api/documents/dossier/${dossier.id}`).then(r => r.data).catch(() => []),
      api.get(`/api/documents/attachments/${dossier.id}`).then(r => r.data).catch(() => ({})),
      api.get(`/api/dossiers/${dossier.id}/timeline`).then(r => r.data).catch(() => []),
    ]).then(([d, a, t]) => {
      setDocs(Array.isArray(d) ? d : []);
      setAttachments(a || {});
      setTimeline(Array.isArray(t) ? t : []);
      setLoading(false);
    });
  }, [dossier]);

  if (!dossier) return null;

  const frmInfo = formalistesMap?.[dossier.formaliste_id];
  const frmCount = frmInfo?.dossiers_actifs;
  const frmPct = frmCount != null ? frmCount / OP_CAPACITE : 0;
  const frmColor = frmPct >= 1 ? D.red : frmPct >= 0.75 ? D.amber : D.green;

  const handleDownload = async (docId) => {
    try {
      const res = await api.get(`/api/documents/download/${docId}`);
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch(e) { console.error(e); }
  };

  const handleUpload = async () => {
    if (!uploadFiles.length || !uploadCat) {
      setUploadError('Choisir une catégorie et au moins un fichier');
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      // Mapping vers le label backend exact (compatible DOCUMENT_TYPES de documents.py)
      const labelMap = {
        'Synthèse': 'Synthèse INPI',
        'Autres documents': 'Autre',
      };
      const subcategory = labelMap[uploadCat] || uploadCat;
      const errors = [];
      // Upload séquentiel pour ne pas saturer le backend
      for (const file of uploadFiles) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          // L'endpoint attend subcategory en query string, pas form-data
          await api.post(
            `/api/documents/attachment/${dossier.id}?subcategory=${encodeURIComponent(subcategory)}`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } catch (e) {
          errors.push(`${file.name} : ${e?.response?.data?.detail || 'échec'}`);
        }
      }
      // Reset + refresh
      setUploadFiles([]);
      if (errors.length === 0) {
        setShowUpload(false);
      } else {
        setUploadError(`Certains fichiers ont échoué : ${errors.join(', ')}`);
      }
      await loadAttachments();
    } catch (e) {
      setUploadError(e?.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const InfoCard = ({ label, value }) => value ? (
    <div style={{ background:D.s2, borderRadius:'6px', padding:'8px 10px', border:`1px solid ${D.b1}` }}>
      <div style={{ fontSize:'9px', textTransform:'uppercase', color:D.tx3, letterSpacing:'0.04em', marginBottom:'2px' }}>{label}</div>
      <div style={{ fontSize:'12px', color:D.tx, fontWeight:500 }}>{value}</div>
    </div>
  ) : null;

  // ─── Carte éditable inline ─────────────────────────────────────────────
  // type='text'|'select'|'date'|'number' ; options pour select : [{value, label}]
  // Note : EditableCard est un composant EXTERNE (au-dessus) pour éviter
  // les pertes de focus dues aux re-render du parent à chaque keystroke.
  // On l'utilise comme un composant normal : <EditableCard label=... />
  const renderField = (label, fieldKey, type = 'text', options, placeholder = '—') => {
    // Pour les inputs type="date", on attend YYYY-MM-DD
    // created_at arrive en ISO datetime depuis l'API → on extrait juste la date
    let displayValue = cur[fieldKey] ?? '';
    if (type === 'date' && displayValue) {
      try {
        displayValue = new Date(displayValue).toISOString().slice(0, 10);
      } catch { /* garde la valeur brute */ }
    }
    return (
      <EditableCardImpl
        key={fieldKey}
        label={label}
        value={displayValue}
        isDirty={fieldKey in edits}
        type={type}
        options={options}
        placeholder={placeholder}
        onChange={(v) => setField(fieldKey, v)}
      />
    );
  };

  // Options pour les selects
  const statusOpts = Object.keys(OP_STATUS_LABELS).map(k => ({ value: k, label: OP_STATUS_LABELS[k] }));
  const typeOpts = [
    { value: 'creation_sas', label: 'Création SAS' },
    { value: 'creation_sarl', label: 'Création SARL' },
    { value: 'creation_sasu', label: 'Création SASU' },
    { value: 'creation_eurl', label: 'Création EURL' },
    { value: 'creation_sci', label: 'Création SCI' },
    { value: 'creation_ei', label: 'Création EI' },
    { value: 'modification', label: 'Modification' },
    { value: 'dissolution', label: 'Dissolution' },
    { value: 'radiation', label: 'Radiation' },
    { value: 'transfert_siege', label: 'Transfert siège' },
    { value: 'depot_comptes', label: 'Dépôt comptes' },
    { value: 'autre', label: 'Autre' },
  ];
  const prioriteOpts = [
    { value: 'normal', label: 'Normal' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'basse', label: 'Basse' },
  ];
  const facturationOpts = [
    { value: 'Payé', label: 'Payé' },
    { value: 'À facturer', label: 'À facturer' },
    { value: 'Facturé', label: 'Facturé' },
    { value: 'En attente', label: 'En attente' },
  ];
  const formalisteOpts = formalistesOptions.map(f => ({
    value: f.id,
    label: `${f.prenom || ''} ${f.nom || ''} (${f._group})`.trim(),
  }));

  // Regroupement de tous les attachments par catégorie métier admin
  const docsByCategory = {};
  ADMIN_DOC_CATEGORIES.forEach(c => { docsByCategory[c] = []; });
  Object.values(attachments).forEach(items => {
    if (!Array.isArray(items)) return;
    items.forEach(att => {
      const cat = adminClassifyDoc(att);
      if (!docsByCategory[cat]) docsByCategory[cat] = [];
      docsByCategory[cat].push(att);
    });
  });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }} onClick={onClose}>
      <div style={{ background:D.s1, borderRadius:'16px', padding:'1.5rem', width:'100%', maxWidth:'720px', maxHeight:'85vh', overflowY:'auto', border:`1px solid ${D.b1}` }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1rem' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px', flexWrap:'wrap' }}>
              <span style={{ fontSize:'11px', color:D.tx3, fontFamily:'monospace' }}>{dossier.reference}</span>
              <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px', background:'rgba(212,168,67,0.12)', color:D.or, fontWeight:500 }}>
                {(cur.type || '').replace(/_/g, ' ')}
              </span>
              <OpStatusBadge status={cur.status} />
            </div>
            <input
              type="text"
              value={cur.societe_nom || ''}
              onChange={e => setField('societe_nom', e.target.value)}
              placeholder="Nom de la société"
              style={{
                background:'transparent', border:'none', borderBottom:`1px solid transparent`,
                color:D.tx, fontSize:'17px', fontWeight:500, fontFamily:'inherit',
                outline:'none', width:'100%', padding:'2px 0',
                transition:'border-color 0.15s',
              }}
              onFocus={e => e.target.style.borderBottomColor = D.or}
              onBlur={e => e.target.style.borderBottomColor = 'transparent'}
            />
            <div style={{ display:'flex', alignItems:'center', gap:'4px', marginTop:'2px' }}>
              <span style={{ fontSize:'12px', color:D.tx3 }}>SIREN</span>
              <input
                type="text"
                value={cur.societe_siren || ''}
                onChange={e => setField('societe_siren', e.target.value)}
                placeholder="—"
                maxLength={9}
                style={{
                  background:'transparent', border:'none', borderBottom:`1px solid transparent`,
                  color:D.tx3, fontSize:'12px', fontFamily:'inherit',
                  outline:'none', width:'90px', padding:'1px 0',
                  transition:'border-color 0.15s',
                }}
                onFocus={e => e.target.style.borderBottomColor = D.or}
                onBlur={e => e.target.style.borderBottomColor = 'transparent'}
              />
            </div>
            {dossier.workspace_name && <div style={{ fontSize:'11px', color:D.or, marginTop:'2px' }}>Client : {dossier.workspace_name}</div>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'18px', color:D.tx2, marginLeft:'12px' }}>×</button>
        </div>

        {/* Formaliste capacity banner */}
        {dossier.formaliste_nom && frmCount != null && (
          <div style={{ background:`${frmColor}10`, border:`1px solid ${frmColor}30`, borderRadius:'6px', padding:'6px 10px', marginBottom:'12px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <OpFrmAvatar nom={dossier.formaliste_nom} count={frmCount} />
            <span style={{ fontSize:'10px', color:frmColor, fontWeight:500 }}>{frmCount}/{OP_CAPACITE} dossiers</span>
          </div>
        )}

        {/* Info cards grid (TOUS éditables) */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'14px' }}>
          {renderField('Date du dossier', 'created_at', 'date')}
          {renderField('Statut', 'status', 'select', statusOpts)}
          {renderField('Type formalité', 'type', 'select', typeOpts)}
          {renderField('Formaliste', 'formaliste_id', 'select', formalisteOpts, 'Non assigné')}
          {renderField('Greffe', 'greffe')}
          {renderField('Cabinet BtoB', 'btob_cabinet')}
          {renderField('Commercial', 'commercial')}
          {renderField('Priorité', 'priorite', 'select', prioriteOpts)}
          {renderField('Facturation', 'etat_facturation', 'select', facturationOpts)}
          {renderField('Débours (€)', 'debours_lexback', 'number', undefined, '0')}
          {renderField('Email client', 'email_client_final', 'email')}
          {renderField('Téléphone', 'telephone_client_final', 'tel')}
          {dossier.sla_deadline && <InfoCard label="SLA" value={opFormatDate(dossier.sla_deadline)} />}
        </div>

        {/* Éléments manquants (éditable) */}
        <div style={{ marginBottom:'12px' }}>
          {renderField('Éléments manquants', 'elements_manquants', 'textarea', undefined, 'Aucun élément manquant signalé')}
        </div>

        {/* Notes formaliste — privé (éditable) */}
        <div style={{ marginBottom:'12px' }}>
          {renderField('Notes formaliste (privé Lexback)', 'notes_formaliste_lexback', 'textarea', undefined, 'Notes internes…')}
        </div>

        {/* Documents / Attachments */}
        {!loading && (
          <div style={{ marginBottom:'12px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
              <div style={{ fontWeight:500, fontSize:'12px', color:D.tx }}>Documents</div>
              <button
                onClick={() => setShowUpload(!showUpload)}
                style={{
                  background: showUpload ? 'rgba(217,168,71,0.18)' : 'rgba(217,168,71,0.12)',
                  border:`0.5px solid ${D.or}`, color:D.or,
                  borderRadius:'6px', padding:'5px 10px',
                  fontSize:'11px', fontWeight:500, cursor:'pointer',
                  display:'inline-flex', alignItems:'center', gap:'4px',
                }}
              >
                {showUpload ? '× Annuler' : '+ Ajouter un document'}
              </button>
            </div>

            {/* Formulaire upload */}
            {showUpload && (
              <div style={{ background:D.s2, border:`0.5px solid ${D.or}`, borderRadius:'8px', padding:'12px', marginBottom:'12px' }}>
                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                  <div>
                    <div style={{ fontSize:'10px', textTransform:'uppercase', color:D.tx3, marginBottom:'4px', letterSpacing:'0.04em' }}>Catégorie</div>
                    <select
                      value={uploadCat}
                      onChange={e => setUploadCat(e.target.value)}
                      style={{
                        width:'100%', background:D.s1, color:D.tx,
                        border:`0.5px solid ${D.b1}`, borderRadius:'6px',
                        padding:'7px 9px', fontSize:'12px',
                      }}
                    >
                      {ADMIN_DOC_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize:'10px', textTransform:'uppercase', color:D.tx3, marginBottom:'4px', letterSpacing:'0.04em' }}>Fichiers <span style={{ color:D.tx3, textTransform:'none', fontSize:'10px' }}>(sélection multiple possible)</span></div>
                    <input
                      type="file"
                      multiple
                      onChange={e => setUploadFiles(Array.from(e.target.files || []))}
                      style={{
                        width:'100%', background:D.s1, color:D.tx,
                        border:`0.5px solid ${D.b1}`, borderRadius:'6px',
                        padding:'7px 9px', fontSize:'12px',
                      }}
                    />
                    {uploadFiles.length > 0 && (
                      <div style={{ marginTop:'6px', display:'flex', flexDirection:'column', gap:'3px' }}>
                        {uploadFiles.map((f, i) => (
                          <div key={i} style={{
                            display:'flex', alignItems:'center', justifyContent:'space-between',
                            fontSize:'11px', color:D.tx2,
                            padding:'4px 8px', background:D.s1,
                            border:`0.5px solid ${D.b1}`, borderRadius:'4px',
                          }}>
                            <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                            <button
                              onClick={() => setUploadFiles(uploadFiles.filter((_, idx) => idx !== i))}
                              style={{ background:'transparent', border:'none', color:D.tx3, cursor:'pointer', padding:'0 4px', fontSize:'14px', flexShrink:0 }}
                              title="Retirer"
                            >×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {uploadError && (
                    <div style={{ fontSize:'11px', color:'#ef4444' }}>{uploadError}</div>
                  )}
                  <div style={{ display:'flex', justifyContent:'flex-end' }}>
                    <button
                      onClick={handleUpload}
                      disabled={uploading || uploadFiles.length === 0}
                      style={{
                        background: uploading || uploadFiles.length === 0 ? 'rgba(217,168,71,0.4)' : D.or,
                        color:'#1f1612', border:'none',
                        borderRadius:'6px', padding:'7px 14px',
                        fontSize:'11.5px', fontWeight:600,
                        cursor: uploading || uploadFiles.length === 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {uploading ? `Envoi…` : uploadFiles.length > 1 ? `Téléverser ${uploadFiles.length} fichiers` : 'Téléverser'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Liste par catégorie (vue par document) */}
            {ADMIN_DOC_CATEGORIES.map(cat => {
              const items = docsByCategory[cat] || [];
              if (items.length === 0) return null;
              return (
                <div key={cat} style={{ marginBottom:'10px' }}>
                  <div style={{ fontSize:'10px', fontWeight:600, textTransform:'uppercase', color:D.tx3, marginBottom:'4px', letterSpacing:'0.04em' }}>{cat}</div>
                  {items.map(att => (
                    <div key={att.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:`0.5px solid ${D.b1}`, gap:'8px' }}>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:'12px', color:D.tx, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{att.nom_original}</div>
                        <div style={{ fontSize:'10px', color:D.tx3 }}>{att.mime_type} · {att.taille_kb} KB</div>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:'10px', flexShrink:0 }}>
                        {att.download_url && <a href={att.download_url} target="_blank" rel="noreferrer" style={{ fontSize:'11px', color:D.blue }}>Télécharger</a>}
                        <button
                          onClick={() => handleDeleteAttachment(att.id)}
                          title="Supprimer ce document"
                          style={{ background:'transparent', border:'none', color:D.red, cursor:'pointer', fontSize:'14px', padding:'2px 4px' }}
                        >🗑</button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Docs plateforme (legacy /api/documents/dossier) */}
            {docs.length > 0 && (
              <div style={{ marginTop:'6px' }}>
                <div style={{ fontSize:'10px', fontWeight:600, textTransform:'uppercase', color:D.tx3, marginBottom:'4px', letterSpacing:'0.04em' }}>Plateforme</div>
                {docs.map(doc => (
                  <div key={doc.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:`0.5px solid ${D.b1}` }}>
                    <div style={{ minWidth:0, flex:1 }}>
                      <div style={{ fontSize:'12px', color:D.tx, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{doc.nom_original}</div>
                      <div style={{ fontSize:'10px', color:D.tx3 }}>{doc.type_document?.replace(/_/g,' ')} · {doc.taille_kb} KB</div>
                    </div>
                    <button onClick={() => handleDownload(doc.id)} style={{ background:'transparent', border:'none', fontSize:'11px', color:D.blue, cursor:'pointer', marginLeft:'8px' }}>Télécharger</button>
                  </div>
                ))}
              </div>
            )}

            {docs.length === 0 && Object.values(docsByCategory).every(items => items.length === 0) && (
              <div style={{ fontSize:'12px', color:D.tx3, fontStyle:'italic' }}>Aucun document</div>
            )}
          </div>
        )}

        {/* Timeline */}
        {timeline.length > 0 && (
          <div style={{ marginBottom:'12px' }}>
            <div style={{ fontWeight:500, fontSize:'12px', color:D.tx, marginBottom:'8px' }}>Historique</div>
            <div style={{ borderLeft:`2px solid rgba(212,168,67,0.2)`, paddingLeft:'12px' }}>
              {timeline.slice(0, 15).map((ev, i) => (
                <div key={i} style={{ position:'relative', paddingBottom:'8px' }}>
                  <div style={{ position:'absolute', left:'-17px', top:'4px', width:'6px', height:'6px', borderRadius:'50%', background:D.or }} />
                  <div style={{ fontSize:'10px', color:D.tx3 }}>
                    {new Date(ev.timestamp).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' })} — {ev.actor || 'Systeme'} · {ev.content || ev.type}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Discussion avec le client */}
        <AdminDiscussion
          dossierId={dossier.id}
          visibility="shared"
          title="Discussion avec le client"
          icon="💬"
        />

        {/* Notes internes Lexback (invisible client) */}
        <AdminDiscussion
          dossierId={dossier.id}
          visibility="internal"
          title="Notes internes Lexback"
          icon="🔒"
        />

        {/* === FOOTER STICKY : Enregistrer + Fermer === */}
        <div style={{
          position:'sticky', bottom:'-1.5rem',
          marginTop:'1rem', marginBottom:'-1.5rem',
          marginLeft:'-1.5rem', marginRight:'-1.5rem',
          padding:'12px 1.5rem',
          background: dirty ? 'rgba(217,168,71,0.10)' : D.s1,
          borderTop:`1px solid ${dirty ? D.or : D.b1}`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          gap:'12px',
          backdropFilter:'blur(8px)',
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', fontSize:'11.5px' }}>
            {dirty ? (
              <span style={{ color:D.or, fontWeight:500 }}>
                ● {Object.keys(edits).length} modification{Object.keys(edits).length > 1 ? 's' : ''} non enregistrée{Object.keys(edits).length > 1 ? 's' : ''}
              </span>
            ) : saveOk ? (
              <span style={{ color:D.green, fontWeight:500 }}>✓ Enregistré</span>
            ) : (
              <span style={{ color:D.tx3 }}>Aucune modification</span>
            )}
            {saveError && <span style={{ color:D.red }}>{saveError}</span>}
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={onClose} style={{ background:'transparent', border:`1px solid ${D.b2}`, borderRadius:'8px', padding:'7px 16px', fontSize:'12px', color:D.tx2, cursor:'pointer' }}>Fermer</button>
            <button
              onClick={handleSaveAll}
              disabled={!dirty || saving}
              style={{
                background: dirty && !saving ? D.or : 'rgba(217,168,71,0.25)',
                border:'none', borderRadius:'8px',
                padding:'7px 18px', fontSize:'12px', fontWeight:600,
                color:'#1f1612',
                cursor: dirty && !saving ? 'pointer' : 'not-allowed',
              }}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Operations main ─────────────────────────────────────────────────────────
function Operations() {
  const { user } = useAuth();
  const { catalog } = useCatalog();
  const canEdit = ['admin', 'formaliste', 'superviseur'].includes(user?.role);
  const [dossiers, setDossiers]   = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [formalistes, setFormalistes] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterStatus, setFilterStatus] = useState('tous');
  const [filterWs, setFilterWs]   = useState('');
  const [filterFrm, setFilterFrm] = useState('');
  const [filterBtob, setFilterBtob] = useState('');
  const [filterFormalityType, setFilterFormalityType] = useState('');
  const [search, setSearch]       = useState('');
  const [debSearch, setDebSearch] = useState('');
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // ── Pagination serveur ──
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal]       = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats]       = useState({ total:0, actifs:0, bloques_rejetes:0, termines:0, en_cours:0, doc_manquant:0 });
  const [jalsByDossier, setJalsByDossier] = useState({});

  // Réinitialise la page quand les filtres changent
  useEffect(() => { setPage(1); }, [filterStatus, filterWs, filterFrm, filterBtob, filterFormalityType, debSearch, pageSize]);

  // Construit les params à envoyer au backend (filtres serveur)
  const buildServerParams = () => {
    const p = {};
    if (filterWs)             p.workspace_id = filterWs;
    if (debSearch)            p.search = debSearch;
    if (filterFrm)            p.formaliste_id = filterFrm;
    if (filterBtob)           p.cabinet_id = filterBtob;
    if (filterFormalityType)  p.formality_type_id = filterFormalityType;
    if (filterStatus && filterStatus !== 'tous') p.status_group = filterStatus;
    return p;
  };

  // ── Édition inline : PATCH /api/admin/dossiers/{id} ──
  const handleAdminInlineUpdate = async (dossierId, payload) => {
    try {
      const res = await api.patch(`/api/admin/dossiers/${dossierId}`, payload);
      // Met à jour la ligne localement
      setDossiers(list => list.map(d => d.id === dossierId ? { ...d, ...res.data } : d));
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  // ── Multi-sélection ──
  const toggleSelect = (id) => {
    setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };
  const clearSelection = () => setSelectedIds([]);

  const handleBulkUpdate = async (payload) => {
    const ids = [...selectedIds];
    let successCount = 0, errorCount = 0;
    for (const id of ids) {
      try { await api.patch(`/api/admin/dossiers/${id}`, payload); successCount++; }
      catch (err) { errorCount++; }
    }
    alert(`${successCount} dossier(s) mis à jour${errorCount > 0 ? `, ${errorCount} erreur(s)` : ''}`);
    clearSelection();
    reloadDossiers();
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const msg1 = `⚠️ Supprimer ${ids.length} dossier${ids.length > 1 ? 's' : ''} définitivement ?\n\nLes attachments et l'historique seront aussi supprimés.\nLes greffes/types/formalistes du catalogue seront conservés.`;
    if (!window.confirm(msg1)) return;
    const msg2 = `Cette action est irréversible. Confirmer la suppression de ${ids.length} dossier${ids.length > 1 ? 's' : ''} ?`;
    if (!window.confirm(msg2)) return;
    try {
      const res = await api.post('/api/admin/dossiers/bulk-delete', { dossier_ids: ids });
      const deleted = res.data?.deleted_count ?? ids.length;
      alert(`✓ ${deleted} dossier${deleted > 1 ? 's' : ''} supprimé${deleted > 1 ? 's' : ''}`);
      clearSelection();
      reloadDossiers();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.detail || err.message));
    }
  };

  // Fonction de rechargement paginée centralisée
  const reloadDossiers = () => {
    const params = { ...buildServerParams(), page, page_size: pageSize };
    api.get('/api/admin/dossiers/paginated', { params })
      .then(r => {
        setDossiers(r.data.items || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.total_pages || 0);
      })
      .catch(() => {});
    // Stats globales (sans status_group, indépendant de la page)
    const statsParams = { ...buildServerParams() };
    delete statsParams.status_group;
    api.get('/api/admin/dossiers/stats', { params: statsParams })
      .then(r => setStats(r.data || {}))
      .catch(() => {});
  };

  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    api.get('/api/admin/workspaces').then(r => setWorkspaces(r.data||[])).catch(()=>{});
    api.get('/api/team/formalistes').then(r => setFormalistes(r.data||[])).catch(()=>{});
  }, []);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = { ...buildServerParams(), page, page_size: pageSize };
    api.get('/api/admin/dossiers/paginated', { params })
      .then(r => {
        if (cancel) return;
        const items = r.data.items || [];
        setDossiers(items);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.total_pages || 0);
        // Fetch JAL summary pour la page courante
        if (items.length > 0) {
          api.post('/api/admin/dossiers/jal-summary', { dossier_ids: items.map(d => d.id) })
            .then(rj => { if (!cancel) setJalsByDossier(rj.data || {}); })
            .catch(()  => { if (!cancel) setJalsByDossier({}); });
        } else {
          setJalsByDossier({});
        }
      })
      .catch(()   => { if (!cancel) { setDossiers([]); setTotal(0); setTotalPages(0); } })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [filterWs, debSearch, filterStatus, filterFrm, filterBtob, filterFormalityType, page, pageSize]);

  // Reload uniquement les JAL d'un dossier (après création/suppression)
  const reloadJalForDossier = (dossierId) => {
    api.post('/api/admin/dossiers/jal-summary', { dossier_ids: [dossierId] })
      .then(rj => setJalsByDossier(prev => ({ ...prev, ...(rj.data || {}), ...((rj.data?.[dossierId] === undefined) ? { [dossierId]: [] } : {}) })))
      .catch(() => {});
  };

  // Stats globales — rechargées en parallèle (sans status_group)
  useEffect(() => {
    const params = { ...buildServerParams() };
    delete params.status_group;
    api.get('/api/admin/dossiers/stats', { params })
      .then(r => setStats(r.data || {}))
      .catch(() => {});
    // eslint-disable-next-line
  }, [filterWs, debSearch, filterFrm, filterBtob, filterFormalityType]);

  const formalistesMap = {};
  formalistes.forEach(f => { formalistesMap[f.id] = f; });

  const btobValues = [...new Set(dossiers.map(d => d.btob_cabinet).filter(Boolean))];

  // Tous les filtres sont désormais appliqués côté serveur.
  // La liste reçue est déjà filtrée + paginée.
  const filtered = dossiers;

  // KPI depuis l'endpoint /dossiers/stats (cohérents avec les filtres serveur).
  // nExpires reste calculé sur la page courante (pas dans /stats faute de colonne directe).
  const nActifs   = stats.actifs ?? 0;
  const nBloques  = stats.bloques_rejetes ?? 0;
  const nTermines = stats.termines ?? 0;
  const nExpires  = dossiers.filter(d => d.sla_status === 'expire' && d.status !== 'termine').length;

  const statusFilters = [
    { key:'tous', label:'Tous' },
    { key:'en_cours', label:'En cours' },
    { key:'termines', label:'Termines' },
    { key:'rejetes', label:'Rejetes' },
    { key:'doc_manquant', label:'Doc manquant' },
  ];

  const opTh = { textAlign:'left', padding:'8px 6px', fontSize:'10px', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', color:D.tx3, borderBottom:`1px solid ${D.b1}` };
  const opTd = { padding:'8px 6px', verticalAlign:'middle' };
  const inpOp = { padding:'5px 8px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'6px', fontSize:'11px', color:D.tx, outline:'none', fontFamily:'DM Sans, sans-serif' };

  return (
    <div style={{ padding:'1.25rem 1.5rem', flex:1 }}>
      <div style={{ marginBottom:'1.25rem' }}>
        <div style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:600, color:D.tx }}>Operations</div>
        <div style={{ fontSize:'11px', color:D.tx3, marginTop:'2px' }}>{total} dossier{total>1?'s':''} · Tous clients confondus</div>
      </div>

      {/* KPI cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'1.25rem' }}>
        <KpiCard label="Dossiers actifs"   value={nActifs}   delta="Toutes LegalTechs" />
        <KpiCard label="Bloques / Rejetes" value={nBloques}  color={nBloques>0?D.red:D.tx2} delta="Action requise" />
        <KpiCard label="SLA expires"       value={nExpires}  color={nExpires>0?D.red:D.tx2} delta="A traiter en urgence" />
        <KpiCard label="Termines"          value={nTermines} color={D.green} delta="Sur la periode" />
      </div>

      {/* Filters bar — pill buttons + dropdowns */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'1rem', flexWrap:'wrap', alignItems:'center' }}>
        {statusFilters.map(f => (
          <button key={f.key} onClick={() => setFilterStatus(f.key)} style={{
            padding:'5px 14px', borderRadius:'999px', fontSize:'12px', cursor:'pointer',
            border:`1px solid ${D.b2}`,
            background: filterStatus === f.key ? D.tx : 'transparent',
            color: filterStatus === f.key ? D.bg : D.tx2,
          }}>{f.label}</button>
        ))}
        <div style={{ flex:1 }} />
        <input
          placeholder="Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inpOp, width:'200px' }}
        />
        <select style={inpOp} value={filterWs} onChange={e => setFilterWs(e.target.value)}>
          <option value="">Client</option>
          {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        {formalistes.length > 0 && (
          <select style={inpOp} value={filterFrm} onChange={e => setFilterFrm(e.target.value)}>
            <option value="">Formaliste</option>
            {formalistes.map(f => <option key={f.id} value={f.id}>{f.prenom} {f.nom}</option>)}
          </select>
        )}
        {catalog?.formality_types?.length > 0 && (
          <select style={inpOp} value={filterFormalityType} onChange={e => setFilterFormalityType(e.target.value)}>
            <option value="">Type</option>
            {catalog.formality_types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        {(catalog?.cabinets?.length > 0 || btobValues.length > 0) && (
          <select style={inpOp} value={filterBtob} onChange={e => setFilterBtob(e.target.value)}>
            <option value="">Cabinet</option>
            {(catalog?.cabinets || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            {btobValues.filter(b => !(catalog?.cabinets || []).some(c => c.name === b)).map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        )}
      </div>

      {/* Bulk actions bar */}
      {canEdit && selectedIds.length > 0 && (
        <div style={{
          background: 'rgba(212,168,67,0.10)',
          border: `1px solid rgba(212,168,67,0.30)`,
          borderRadius: '8px', padding: '8px 12px', marginBottom: '12px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px',
        }}>
          <span style={{ fontSize: '12px', color: D.or, fontWeight: 500 }}>
            {selectedIds.length} dossier{selectedIds.length > 1 ? 's' : ''} sélectionné{selectedIds.length > 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: D.tx2 }}>Changer statut →</span>
            <InlineCatalogPicker theme="dark"
              currentValue={null}
              options={catalog?.status_labels || []}
              onChange={(statusLabelId) => {
                if (statusLabelId && window.confirm(`Appliquer ce statut à ${selectedIds.length} dossier(s) ?`)) {
                  handleBulkUpdate({ status_label_id: statusLabelId });
                }
              }}
              placeholder="Choisir"
            />
            <button onClick={handleBulkDelete} style={{
              background: 'rgba(239,91,91,0.12)', border: `1px solid ${D.red}40`, color: D.red,
              padding: '4px 12px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer',
              fontWeight: 500,
            }}>
              🗑 Supprimer
            </button>
            <button onClick={clearSelection} style={{
              background: 'transparent', border: `1px solid ${D.b1}`, color: D.tx2,
              padding: '4px 12px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer',
            }}>Annuler</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ ...card, overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding:'2rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>Chargement...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:'2rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>Aucun dossier trouve.</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:0, fontSize:'12px' }}>
              <thead>
                <tr>
                  {canEdit && <th style={opTh}>
                    <input type="checkbox"
                      checked={selectedIds.length === filtered.length && filtered.length > 0}
                      onChange={() => {
                        if (selectedIds.length === filtered.length) setSelectedIds([]);
                        else setSelectedIds(filtered.map(d => d.id));
                      }} />
                  </th>}
                  <th style={opTh}></th>
                  <th style={opTh}>Societe</th>
                  <th style={opTh}>Client</th>
                  <th style={opTh} className="admin-hide-mobile">Formalite</th>
                  <th style={opTh} className="admin-hide-mobile">Sous-types</th>
                  <th style={opTh} className="admin-hide-mobile">Date</th>
                  <th style={opTh}>Ref</th>
                  <th style={opTh}>Statut</th>
                  <th style={opTh}>Formaliste</th>
                  <th style={opTh} className="admin-hide-mobile">Greffe</th>
                  <th style={opTh} className="admin-hide-mobile">Cabinet</th>
                  <th style={opTh} className="admin-hide-mobile">JAL</th>
                  <th style={opTh}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(d => {
                  const frmData = d.formaliste_id ? formalistesMap[d.formaliste_id] : null;
                  const frmCount = frmData?.dossiers_actifs;
                  const frmPct = frmCount != null ? frmCount / OP_CAPACITE : -1;
                  const barColor = d.status_label?.color || (frmPct < 0 ? D.tx3 : frmPct >= 1 ? D.red : frmPct >= 0.75 ? D.amber : D.green);
                  const isSelected = selectedIds.includes(d.id);
                  // Sub-types disponibles selon le type
                  const availableSubTypes = (() => {
                    if (!catalog || !d.formality_type_id) return [];
                    const t = (catalog.formality_types || []).find(t => t.id === d.formality_type_id);
                    return t ? (t.sub_types || []) : [];
                  })();

                  return (
                    <tr key={d.id} style={{
                      cursor:'pointer',
                      borderBottom:`0.5px solid ${D.b1}`,
                      background: isSelected ? 'rgba(212,168,67,0.18)' : 'transparent',
                      color: D.tx,
                    }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background=D.s2; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background='transparent'; }}>
                      {canEdit && (
                        <td style={opTd} onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(d.id)} />
                        </td>
                      )}
                      {/* Color bar */}
                      <td style={{ ...opTd, width:'4px', padding:0 }} onClick={() => setSelectedDossier(d)}>
                        <div style={{ width:'4px', height:'100%', minHeight:'36px', background:barColor, borderRadius:'2px' }} />
                      </td>
                      {/* Societe */}
                      <td style={opTd} onClick={() => setSelectedDossier(d)}>
                        <div style={{ fontWeight:500, color:D.tx }}>{d.societe_nom || '—'}</div>
                        {d.societe_siren && <div style={{ fontSize:'10px', color:D.tx3 }}>{d.societe_siren}</div>}
                      </td>
                      {/* Client */}
                      <td style={opTd} onClick={() => setSelectedDossier(d)}>
                        <span style={{ fontSize:'11px', color:D.or }}>{d.workspace_name || '—'}</span>
                      </td>
                      {/* Formalité — éditable */}
                      <td style={opTd} className="admin-hide-mobile" onClick={e => e.stopPropagation()}>
                        {canEdit && catalog ? (
                          <InlineCatalogPicker theme="dark"
                            currentValue={d.formality_type_id}
                            options={catalog.formality_types || []}
                            onChange={(v) => handleAdminInlineUpdate(d.id, { formality_type_id: v })}
                          />
                        ) : (
                          d.formality_type_label
                            ? <CatalogPill label={d.formality_type_label.name} color={d.formality_type_label.color} size="xs" />
                            : <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px', background:'rgba(212,168,67,0.12)', color:D.or, fontWeight:500 }}>{d.type?.replace(/_/g,' ')}</span>
                        )}
                      </td>
                      {/* Sous-types */}
                      <td style={opTd} className="admin-hide-mobile" onClick={e => e.stopPropagation()}>
                        {canEdit && catalog ? (
                          <InlineCatalogPicker theme="dark"
                            currentValue={(d.sub_type_labels || []).map(s => s.id)}
                            options={availableSubTypes}
                            multi
                            onChange={(ids) => handleAdminInlineUpdate(d.id, { sub_type_ids: ids })}
                            disabled={availableSubTypes.length === 0}
                            placeholder="+"
                          />
                        ) : (
                          <CatalogPillsStack items={d.sub_type_labels || []} size="xs" max={2} />
                        )}
                      </td>
                      {/* Date */}
                      <td style={{ ...opTd, fontSize:'11px', color:D.tx2 }} className="admin-hide-mobile" onClick={() => setSelectedDossier(d)}>
                        {opFormatDate(d.created_at)}
                      </td>
                      {/* Ref */}
                      <td style={opTd} onClick={() => setSelectedDossier(d)}>
                        <span style={{ fontFamily:'monospace', fontSize:'11px', color:D.or }}>{d.reference}</span>
                      </td>
                      {/* Statut — éditable */}
                      <td style={opTd} onClick={e => e.stopPropagation()}>
                        {canEdit && catalog ? (
                          <InlineCatalogPicker theme="dark"
                            currentValue={d.status_label_id}
                            options={catalog.status_labels || []}
                            onChange={(v) => handleAdminInlineUpdate(d.id, { status_label_id: v })}
                          />
                        ) : (
                          d.status_label
                            ? <CatalogPill label={d.status_label.name} color={d.status_label.color} emoji={d.status_label.emoji} size="xs" />
                            : <OpStatusBadge status={d.status} />
                        )}
                      </td>
                      {/* Formaliste — éditable */}
                      <td style={opTd} onClick={e => e.stopPropagation()}>
                        {canEdit ? (
                          <FormalistePicker
                            theme="dark"
                            currentValue={d.formaliste_id}
                            currentName={d.formaliste_nom || d.formaliste_airtable}
                            workspaceId={d.workspace_id}
                            onChange={(formalisteId) => handleAdminInlineUpdate(d.id, { formaliste_id: formalisteId })}
                          />
                        ) : d.formaliste_nom ? (
                          <OpFrmAvatar nom={d.formaliste_nom} count={frmCount} />
                        ) : d.formaliste_airtable ? (
                          <span style={{ fontSize:'11px', color:D.tx2 }}>{d.formaliste_airtable}</span>
                        ) : (
                          <span style={{ fontSize:'11px', color:D.tx3, fontStyle:'italic' }}>—</span>
                        )}
                      </td>
                      {/* Greffe — éditable */}
                      <td style={opTd} className="admin-hide-mobile" onClick={e => e.stopPropagation()}>
                        {canEdit && catalog ? (
                          <InlineCatalogPicker theme="dark"
                            currentValue={d.greffe_id}
                            options={catalog.greffes || []}
                            onChange={(v) => handleAdminInlineUpdate(d.id, { greffe_id: v })}
                            placeholder="+"
                          />
                        ) : (
                          <span style={{ fontSize:'11px', color:D.tx2 }}>{d.greffe_label?.name || d.greffe || '—'}</span>
                        )}
                      </td>
                      {/* Cabinet — éditable */}
                      <td style={opTd} className="admin-hide-mobile" onClick={e => e.stopPropagation()}>
                        {canEdit && catalog ? (
                          <InlineCatalogPicker theme="dark"
                            currentValue={d.cabinet_id}
                            options={catalog.cabinets || []}
                            onChange={(v) => handleAdminInlineUpdate(d.id, { cabinet_id: v })}
                            placeholder="+"
                          />
                        ) : (
                          d.cabinet_label
                            ? <CatalogPill label={d.cabinet_label.name} color={d.cabinet_label.color} size="xs" />
                            : <span style={{ fontSize:'11px', color:D.tx2 }}>{d.btob_cabinet || '—'}</span>
                        )}
                      </td>
                      {/* JAL */}
                      <td style={opTd} className="admin-hide-mobile" onClick={e => e.stopPropagation()}>
                        <JalCell
                          dossier={d}
                          jalList={jalsByDossier[d.id] || []}
                          onChanged={() => reloadJalForDossier(d.id)}
                        />
                      </td>
                      {/* Action */}
                      <td style={opTd}>
                        <button onClick={e => { e.stopPropagation(); setSelectedDossier(d); }} style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:'14px', color:D.tx3 }}>⋯</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {!loading && total > 0 && (
          <div style={{
            display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px',
            marginTop:'14px', padding:'10px 0', borderTop:`1px solid ${D.b1}`,
          }}>
            <div style={{ fontSize:'11px', color:D.tx3 }}>
              {total === 0 ? '0 résultat' : (
                <>
                  Affichage <span style={{ color:D.tx2 }}>{(page-1)*pageSize + 1}–{Math.min(page*pageSize, total)}</span>
                  {' '}sur <span style={{ color:D.tx2 }}>{total}</span>
                </>
              )}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <span style={{ fontSize:'11px', color:D.tx3 }}>Par page</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(parseInt(e.target.value, 10))}
                style={{ ...inpOp, padding:'4px 8px' }}
              >
                {[25, 50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                onClick={() => setPage(1)}
                disabled={page <= 1}
                style={{ ...inpOp, cursor: page<=1 ? 'not-allowed' : 'pointer', opacity: page<=1 ? 0.4 : 1 }}
              >« Début</button>
              <button
                onClick={() => setPage(p => Math.max(1, p-1))}
                disabled={page <= 1}
                style={{ ...inpOp, cursor: page<=1 ? 'not-allowed' : 'pointer', opacity: page<=1 ? 0.4 : 1 }}
              >‹ Préc.</button>
              <span style={{ fontSize:'11px', color:D.tx, padding:'0 6px' }}>
                Page <span style={{ color:D.or, fontWeight:600 }}>{page}</span> / {totalPages || 1}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages || 1, p+1))}
                disabled={page >= totalPages}
                style={{ ...inpOp, cursor: page>=totalPages ? 'not-allowed' : 'pointer', opacity: page>=totalPages ? 0.4 : 1 }}
              >Suiv. ›</button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                style={{ ...inpOp, cursor: page>=totalPages ? 'not-allowed' : 'pointer', opacity: page>=totalPages ? 0.4 : 1 }}
              >Fin »</button>
            </div>
          </div>
        )}
      </div>

      <AdminDossierDetailModal dossier={selectedDossier} onClose={() => setSelectedDossier(null)} formalistesMap={formalistesMap} />

      <style>{`
        @media (max-width: 768px) {
          .admin-hide-mobile { display: none !important; }
        }
      `}</style>
    </div>
  );
}

// ─── ALERTES ─────────────────────────────────────────────────────────────────
function Alertes() {
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all'); // all | crit | warn | info

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get('/api/admin/alertes')
      .then(r => { if (!cancel) setAlertes(r.data||[]); })
      .catch(() => { if (!cancel) setAlertes([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const C = {
    crit: { border:'rgba(239,91,91,0.25)', bg:'rgba(239,91,91,0.06)', dot:D.red,   btn:D.red },
    warn: { border:'rgba(240,168,67,0.25)', bg:'rgba(240,168,67,0.06)', dot:D.amber, btn:D.amber },
    info: { border:'rgba(91,141,239,0.2)',  bg:'rgba(91,141,239,0.05)', dot:D.blue,  btn:D.blue },
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'à l\'instant';
    if (diff < 3600) return `il y a ${Math.floor(diff/60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff/3600)} h`;
    return `il y a ${Math.floor(diff/86400)} j`;
  };

  const filtered = filter === 'all' ? alertes : alertes.filter(a => a.level === filter);
  const nCrit    = alertes.filter(a => a.level === 'crit').length;
  const nWarn    = alertes.filter(a => a.level === 'warn').length;
  const nInfo    = alertes.filter(a => a.level === 'info').length;

  const tabBtn = (active) => ({
    background: active ? D.s2 : 'transparent',
    border: `1px solid ${active ? D.b1 : 'transparent'}`,
    color: active ? D.tx : D.tx3,
    padding:'5px 12px', fontSize:'11px', borderRadius:'6px', cursor:'pointer',
    fontFamily:'DM Sans, sans-serif',
  });

  return (
    <div style={{ padding:'1.25rem 1.5rem', flex:1 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem' }}>
        <div>
          <div style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:600, color:D.tx }}>Alertes</div>
          <div style={{ fontSize:'11px', color:D.tx3, marginTop:'2px' }}>{nCrit} critique{nCrit>1?'s':''} · {nWarn} avertissement{nWarn>1?'s':''} · {nInfo} info</div>
        </div>
        <div style={{ display:'flex', gap:'6px' }}>
          <button style={tabBtn(filter==='all')}  onClick={() => setFilter('all')}>Toutes ({alertes.length})</button>
          <button style={tabBtn(filter==='crit')} onClick={() => setFilter('crit')}>Critiques ({nCrit})</button>
          <button style={tabBtn(filter==='warn')} onClick={() => setFilter('warn')}>Avertissements ({nWarn})</button>
        </div>
      </div>

      {loading ? (
        <div style={{ ...card, padding:'2rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, padding:'2rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>
          {alertes.length === 0 ? 'Aucune alerte active ✓' : 'Aucune alerte avec ce filtre.'}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {filtered.map((a,i) => {
            const c = C[a.level] || C.info;
            return (
              <div key={i} style={{ border:`1px solid ${c.border}`, background:c.bg, borderRadius:'10px', padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:c.dot, flexShrink:0 }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'12px', fontWeight:500, color:D.tx, marginBottom:'3px' }}>{a.titre}</div>
                  <div style={{ fontSize:'11px', color:D.tx2, lineHeight:1.5 }}>{a.desc}</div>
                </div>
                <div style={{ fontSize:'10px', color:D.tx3, whiteSpace:'nowrap', marginRight:'8px' }}>{timeAgo(a.created_at)}</div>
                <button style={{ background:`${c.btn}18`, color:c.btn, border:`1px solid ${c.btn}40`, padding:'5px 12px', fontSize:'11px', borderRadius:'6px', cursor:'pointer', fontFamily:'DM Sans, sans-serif', whiteSpace:'nowrap', fontWeight:500 }}>
                  {a.action}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── FACTURATION ADMIN ───────────────────────────────────────────────────────
function FacturationAdmin() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get('/api/admin/billing')
      .then(r => { if (!cancel) setBilling(r.data); })
      .catch(() => { if (!cancel) setBilling(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const rows        = billing?.rows || [];
  const totalCA     = billing?.total_forfait || 0;
  const totalDebours = billing?.total_debours || 0;
  const totalGlobal = billing?.total_global || 0;

  const monthNames = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const monthLabel = billing ? `${monthNames[billing.month-1]} ${billing.year}` : '';

  return (
    <div style={{ padding:'1.25rem 1.5rem', flex:1 }}>
      <div style={{ marginBottom:'1.25rem' }}>
        <div style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:600, color:D.tx }}>Facturation</div>
        <div style={{ fontSize:'11px', color:D.tx3, marginTop:'2px' }}>Récapitulatif mensuel{monthLabel?` · ${monthLabel}`:''}</div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'1.25rem' }}>
        <KpiCard label="CA forfaits"      value={`${totalCA.toLocaleString('fr-FR')} €`}      color={D.green} delta="Abonnements" />
        <KpiCard label="Débours INPI"     value={`${totalDebours.toLocaleString('fr-FR')} €`} delta="Frais greffe + JAL" />
        <KpiCard label="Total facturable" value={`${totalGlobal.toLocaleString('fr-FR')} €`}  color={D.or}    delta="HT ce mois" />
      </div>

      <div style={{ ...card, overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding:'2rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>Chargement…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding:'2rem', textAlign:'center', color:D.tx3, fontSize:'12px' }}>Aucun client à facturer.</div>
        ) : (
          <>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:`1px solid ${D.b1}` }}>
                  {['Client','Plan','ETP','Forfait','Débours','Total HT','Statut'].map(h => (
                    <th key={h} style={{ textAlign: h==='Total HT'||h==='Forfait'||h==='Débours'?'right':'left', padding:'10px 14px', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.07em', color:D.tx3, fontWeight:400 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r,i) => (
                  <tr key={r.id} style={{ borderBottom:i<rows.length-1?`1px solid ${D.b1}`:'none' }}
                    onMouseEnter={e => e.currentTarget.style.background=D.s2}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    <td style={{ padding:'10px 14px', fontSize:'12px', fontWeight:500, color:D.tx }}>{r.name}</td>
                    <td style={{ padding:'10px 14px' }}><Badge label={r.plan} color={{enterprise:D.or,pro:D.blue,starter:D.tx3}[r.plan]||D.tx3} /></td>
                    <td style={{ padding:'10px 14px', fontSize:'12px', color:D.tx2 }}>{r.etp} ETP</td>
                    <td style={{ padding:'10px 14px', fontSize:'12px', color:D.tx2, textAlign:'right' }}>{r.forfait.toLocaleString('fr-FR')} €</td>
                    <td style={{ padding:'10px 14px', fontSize:'12px', color:D.tx2, textAlign:'right' }}>{r.debours.toLocaleString('fr-FR')} €</td>
                    <td style={{ padding:'10px 14px', fontSize:'12px', fontWeight:600, color:D.or, textAlign:'right' }}>{r.total.toLocaleString('fr-FR')} €</td>
                    <td style={{ padding:'10px 14px' }}><Badge label={r.statut} color={D.amber} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding:'10px 14px', borderTop:`1px solid ${D.b1}`, display:'flex', justifyContent:'flex-end', gap:'1rem' }}>
              <div style={{ fontSize:'11px', color:D.tx3 }}>Total HT : <strong style={{ color:D.or, fontFamily:'Syne, sans-serif', fontSize:'14px' }}>{totalGlobal.toLocaleString('fr-FR')} €</strong></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── PARAMETRES ──────────────────────────────────────────────────────────────
function Parametres() {
  const [activeSubTab, setActiveSubTab] = useState('systeme');
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get('/api/admin/stats').then(r => setStats(r.data)).catch(()=>{});
  }, []);

  const SUB_TABS = [
    { id: 'systeme',   label: 'Système' },
    { id: 'catalogue', label: 'Catalogue' },
    { id: 'backup',    label: 'Backup Airtable' },
  ];

  return (
    <div style={{ padding:'1.25rem 1.5rem', flex:1 }}>
      <div style={{ marginBottom:'1.25rem' }}>
        <div style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:600, color:D.tx }}>Paramètres</div>
      </div>

      {/* Onglets horizontaux */}
      <div style={{ display:'flex', gap:'2px', borderBottom:`1px solid ${D.b1}`, marginBottom:'20px' }}>
        {SUB_TABS.map(tab => (
          <div key={tab.id} onClick={() => setActiveSubTab(tab.id)} style={{
            padding:'8px 14px', cursor:'pointer', fontSize:'12px',
            color: activeSubTab === tab.id ? D.or : D.tx2,
            borderBottom: activeSubTab === tab.id ? `2px solid ${D.or}` : '2px solid transparent',
            marginBottom:'-1px',
            transition:'all 0.12s',
            fontWeight: activeSubTab === tab.id ? 600 : 400,
          }}>
            {tab.label}
          </div>
        ))}
      </div>

      {activeSubTab === 'systeme' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
          <div style={{ ...card, padding:'1rem' }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx, marginBottom:'12px' }}>Informations système</div>
            {[
              ['Version API',    'v1.1.0'],
              ['Environnement',  'Production'],
              ['Base de données','PostgreSQL · Railway'],
              ['Cache',          'Redis · Actif'],
              ['INPI Robot',     'Playwright · Actif'],
            ].map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:`1px solid ${D.b1}`, fontSize:'11px' }}>
                <span style={{ color:D.tx3 }}>{k}</span>
                <span style={{ color:D.green, fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ ...card, padding:'1rem' }}>
            <div style={{ fontFamily:'Syne, sans-serif', fontSize:'13px', fontWeight:600, color:D.tx, marginBottom:'12px' }}>Stats globales</div>
            {[
              ['Total workspaces', stats?.workspaces ?? '—'],
              ['Total dossiers',   stats?.dossiers ?? '—'],
              ['Users actifs',     stats?.users ?? '—'],
              ['Users désactivés', stats?.users_inactive ?? '—'],
            ].map(([k,v]) => (
              <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:`1px solid ${D.b1}`, fontSize:'11px' }}>
                <span style={{ color:D.tx3 }}>{k}</span>
                <span style={{ color:D.tx, fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSubTab === 'catalogue' && <CatalogueContent withHeader={false} />}
      {activeSubTab === 'backup' && <PlatformBackup />}
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Admin() {
  const [active, setActive] = useState('overview');
  const [badges, setBadges] = useState({});

  useEffect(() => {
    let cancel = false;
    // Badge Opérations : nb dossiers actions requises (bloque + rejete + doc_manquant)
    api.get('/api/admin/dossiers/stats')
      .then(r => {
        if (cancel) return;
        const n = (r.data?.bloques_rejetes || 0) + (r.data?.doc_manquant || 0);
        setBadges(b => ({ ...b, operations: n }));
      })
      .catch(() => {});
    // Badge Alertes : nb d'alertes critiques
    api.get('/api/admin/alertes')
      .then(r => {
        if (cancel) return;
        const list = r.data || [];
        const n = list.filter(a => a.level === 'crit').length;
        setBadges(b => ({ ...b, alertes: n }));
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [active]);  // refresh quand on change de page

  return (
    <AdminLayout active={active} setActive={setActive} badges={badges}>
      {active==='overview'    && <Overview setActive={setActive} />}
      {active==='clients'     && <Clients />}
      {active==='equipes'     && <Equipes />}
      {active==='operations'  && <Operations />}
      {active==='alertes'     && <Alertes />}
      {active==='facturation' && <FacturationAdmin />}
      {active==='parametres'  && <Parametres />}
    </AdminLayout>
  );
}
